import React, { useState, useEffect, useRef } from 'react';
import { TextField, Button, Grid, Checkbox, FormControlLabel, Typography, Modal, TableFooter, TablePagination, InputLabel, FormControl, CircularProgress, FormLabel, FormGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Autocomplete, Select, MenuItem, Box, InputAdornment } from '@mui/material';
import { IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSucursal } from '../contexts/SucursalContext';
import GestionClientes from '../Clientes/GestionClientes'; // Importa tu componente GestionClientes
import GestionOrdenTrabajo from './GestionOrdenTrabajo'; // Importa el componente GestionOrdenTrabajo
import { useUser } from '../contexts/UserContext';
import ResultadoModal from '../components/ResultadoModal';
import { debounce } from 'lodash'; // Puedes usar lodash para crear la función debounce
import { useMemo, useCallback } from 'react';
import { Tooltip } from '@mui/material';
import CondicionesUtilidadesModal from './CondicionesUtilidadesModal'; // Ajusta la ruta
import VisibilityIcon from '@mui/icons-material/Visibility';
import Swal from 'sweetalert2';
import GestorDescuentos from './GestorDescuentos';
import { useAccesos } from '../contexts/AccesosContext';

const swalWithHighZIndex = Swal.mixin({
    zIndex: 9999
});

const normalizeText = (text = '') =>
    text
        .toString()
        .toLowerCase()
        .normalize('NFD') // separa acentos
        .replace(/[\u0300-\u036f]/g, '') // elimina acentos
        .replace(/[^a-z0-9\s/-]/g, ' ') // reemplaza símbolos/puntuación por espacio
        .replace(/\s+/g, ' ') // compacta múltiples espacios
        .trim();

// Filter inteligente con coincidencias secuenciales y palabras, devolviendo referencias originales


const createSmartFilter = (searchFields, maxResults = 100) => {
  return (options, { inputValue }) => {
    if (!inputValue || inputValue.length < 1) {
      return options.slice(0, Math.min(maxResults, 50)); // primeros 50 sin búsqueda
    }

    const normalizedInput = normalizeText(inputValue);
    const words = normalizedInput.split(/\s+/).filter(w => w.length > 0);

    if (words.length === 0) {
      return options.slice(0, Math.min(maxResults, 50));
    }

    // 🔍 1️⃣ Coincidencias exactas de la frase completa
    const exactPhraseMatches = options.filter(option => {
      return searchFields.some(field => {
        const fieldValue = normalizeText(
          typeof field === "function" ? field(option) : option[field]
        );
        return fieldValue.includes(normalizedInput);
      });
    });

    // ✅ Si hay coincidencias exactas de frase, devolvemos solo esas
    if (exactPhraseMatches.length > 0) {
      return exactPhraseMatches.slice(0, maxResults);
    }

    // 🔍 2️⃣ Si no hay coincidencias exactas, usamos puntuación más flexible
    const scoredOptions = options
      .map(option => {
        let score = 0;
        let wordMatches = 0;
        let sequentialMatches = 0;

        searchFields.forEach(field => {
          const fieldValue = normalizeText(
            typeof field === "function" ? field(option) : option[field]
          );
          if (!fieldValue) return;

          // Coincidencias secuenciales ("las acacias" en orden correcto)
          if (words.length > 1) {
            let lastIndex = -1;
            let inSequence = true;
            for (let i = 0; i < words.length; i++) {
              const idx = fieldValue.indexOf(words[i], lastIndex + 1);
              if (idx > lastIndex) {
                lastIndex = idx;
              } else {
                inSequence = false;
                break;
              }
            }
            if (inSequence) {
              sequentialMatches++;
              score += 10000;
              if (fieldValue.startsWith(words[0])) score += 5000;
            }
          }

          // Coincidencias palabra a palabra
          const fieldWords = fieldValue.split(/\s+/);
          let foundWords = 0;

          words.forEach(word => {
            if (word.length < 2) return; // ignorar palabras cortas
            if (fieldValue.startsWith(word)) {
              score += 3000;
              foundWords++;
            } else if (fieldWords.some(w => w.startsWith(word))) {
              score += 1500;
              foundWords++;
            } else if (fieldWords.includes(word)) {
              score += 1000;
              foundWords++;
            } else if (fieldValue.includes(word)) {
              score += 200;
              foundWords++;
            }
          });

          wordMatches += foundWords;
        });

        const allWordsFound =
          wordMatches >= words.length || sequentialMatches > 0;

        // Penalizar si no se encontraron todas las palabras
        if (!allWordsFound) score = 0;

        return { option, score, allWordsFound, sequentialMatches };
      })
      .filter(item => item.score > 0 && item.allWordsFound) // 👈 sólo coincidencias completas
      .sort((a, b) => {
        // Priorizar secuenciales y luego score alto
        if (a.sequentialMatches !== b.sequentialMatches)
          return b.sequentialMatches - a.sequentialMatches;
        if (b.score !== a.score) return b.score - a.score;

        // En empate, preferir texto más corto (más específico)
        const aText = normalizeText(
          typeof searchFields[0] === "function"
            ? searchFields[0](a.option)
            : a.option[searchFields[0]]
        );
        const bText = normalizeText(
          typeof searchFields[0] === "function"
            ? searchFields[0](b.option)
            : b.option[searchFields[0]]
        );
        return aText.length - bText.length;
      })
      .slice(0, maxResults)
      .map(item => item.option);

    return scoredOptions;
  };
};

const STOP_WORDS = new Set([
    'de', 'del', 'la', 'el', 'los', 'las',
    'y', 'o', 'u', 'en', 'para', 'por', 'con'
]);


// Filtro específico para casos donde queremos búsqueda muy restrictiva
const createRestrictiveFilter = (searchFields, maxResults = 15, analisisAgregados = []) => {
    return (options, { inputValue }) => {
         // 🔒 FILTRAR ANÁLISIS YA AGREGADOS desde el inicio
        const idsAgregados = new Set(analisisAgregados.map(a => a.idTipoAnalisis));
        const opcionesDisponibles = options.filter(option => !idsAgregados.has(option.idTipoAnalisis));
        
        if (!inputValue || inputValue.length < 1) {
            return opcionesDisponibles.slice(0, Math.min(maxResults, 10));
        }
        
        const normalizedInput = normalizeText(inputValue);
        const words = normalizedInput.split(/\s+/).filter(word => word.length > 0 && !STOP_WORDS.has(word));
        
        if (words.length === 0) return opcionesDisponibles.slice(0, Math.min(maxResults, 10));
        
        const hasSequenceSearch = normalizedInput.trim().includes(' ');

        // 🔹 NUEVO: Filtrar por coincidencia exacta de secuencia (prioridad absoluta)
        if (hasSequenceSearch) {
            const sequenceMatches = opcionesDisponibles.filter(option =>
                searchFields.some(field => {
                    const fieldValue = normalizeText(
                        typeof field === 'function' ? field(option) : option[field]
                    );
                    return fieldValue?.includes(normalizedInput);
                })
            );

            if (sequenceMatches.length > 0) {
                return sequenceMatches.slice(0, maxResults);
            }
        }

        const scoredOptions = opcionesDisponibles
            .map(option => {
                let score = 0;
                let exactPhraseMatches = 0;
                let wordMatches = 0;
                let allWordsFound = true;
                let sequentialMatches = 0;
                let startsWithInput = false;
                
                // Evaluar cada campo de búsqueda
                searchFields.forEach(field => {
                    const fieldValue = normalizeText(typeof field === 'function' ? field(option) : option[field]);
                    if (!fieldValue) return;
                    
                     // 🎯 BONUS ESPECIAL: Para búsquedas cortas (3 letras o menos), dar prioridad absoluta a coincidencias exactas
                    if (normalizedInput.length <= 3) {
                        // Coincidencia exacta completa
                        if (fieldValue === normalizedInput) {
                            score += 1000000; // Prioridad máxima absoluta
                            exactPhraseMatches++;
                            return;
                        }
                        // Empieza exactamente con la búsqueda
                        if (fieldValue.startsWith(normalizedInput + ' ')) {
                            score += 500000;
                            startsWithInput = true;
                        }
                    }
                    
                    // 1. MÁXIMA PRIORIDAD: Búsqueda de frase exacta completa
                    if (words.length > 1 && fieldValue.includes(normalizedInput)) {
                        exactPhraseMatches++;
                        score += 100000; // Puntuación máxima para frase exacta
                        
                        // Bonus masivo si la frase está al inicio
                        if (fieldValue.startsWith(normalizedInput)) {
                            score += 50000;
                        }
                        return; // Si encontramos la frase exacta, es suficiente
                    }
                    
                    // 2. SEGUNDA PRIORIDAD: Verificar si todas las palabras aparecen en orden secuencial
                    if (words.length > 1) {
                        let lastFoundIndex = -1;
                        let wordsInSequence = 0;
                        let isSequential = true;
                        
                        for (let i = 0; i < words.length; i++) {
                            const wordIndex = fieldValue.indexOf(words[i], lastFoundIndex + 1);
                            if (wordIndex > lastFoundIndex) {
                                lastFoundIndex = wordIndex;
                                wordsInSequence++;
                            } else {
                                isSequential = false;
                                break;
                            }
                        }
                        
                        if (isSequential && wordsInSequence === words.length) {
                            sequentialMatches++;
                            score += 50000; // Alto puntaje para palabras en secuencia
                            
                            // Bonus si la secuencia empieza al inicio del campo
                            if (fieldValue.startsWith(words[0])) {
                                score += 25000;
                            }
                        }
                    }
                    
                    // 3. TERCERA PRIORIDAD: Evaluar palabras individuales
                    const fieldWords = fieldValue.split(/\s+/);

                    // 🔒 Para inputs muy cortos (ej: T4), exigir match exacto de palabra
                if (normalizedInput.length <= 3) {
                    if (!fieldWords.includes(normalizedInput)) {
                        return;
                    }
                }

                    let foundWordsInField = 0;
                    
                    words.forEach((word, index) => {
                        if (word.length < 1) return; // Permitir palabras más cortas en restrictivo
                        
                        let wordFound = false;
                        
                        // Coincidencia exacta al inicio del campo (máxima puntuación individual)
                        if (fieldValue.startsWith(word)) {
                            score += 10000;
                            wordFound = true;
                        }
                        // Coincidencia exacta al inicio de cualquier palabra del campo
                        else if (fieldWords.some(w => w.startsWith(word))) {
                            score += 5000;
                            wordFound = true;
                        }
                        // Coincidencia de palabra completa
                        else if (fieldWords.some(w => w === word)) {
                            score += 2000;
                            wordFound = true;
                        }
                        // Contiene la palabra (menor puntuación)
                        else if (fieldValue.includes(word)) {
                            score += 500;
                            wordFound = true;
                        }
                        
                        if (wordFound) {
                            foundWordsInField++;
                            wordMatches++;
                            
                            // Bonus por posición: primeras palabras valen más
                            if (index === 0) {
                                score += 1000; // Primera palabra vale más
                            } else if (index === 1) {
                                score += 500; // Segunda palabra
                            }
                        }
                    });
                    
                    // Bonus si se encontraron todas las palabras en este campo
                    if (foundWordsInField === words.length) {
                        score += 5000 * words.length;
                    }
                });
                
                // Verificar si se encontraron todas las palabras buscadas
                allWordsFound = wordMatches >= words.length || exactPhraseMatches > 0 || sequentialMatches > 0;
                
                // BONUS GLOBALES
                // Bonus masivo por coincidencia de frase exacta
                if (exactPhraseMatches > 0) {
                    score += 20000 * exactPhraseMatches;
                }
                
                // Bonus por palabras secuenciales
                if (sequentialMatches > 0) {
                    score += 10000 * sequentialMatches;
                }
                
                // Bonus si todas las palabras fueron encontradas
                if (allWordsFound) {
                    score += 2000 * words.length;
                }
                
                // PENALIZACIONES
                // Penalización severa si no se encontraron todas las palabras
                const missingWords = Math.max(0, words.length - Math.floor(wordMatches / searchFields.length));
                if (missingWords > 0) {
                    score = Math.max(0, score - (missingWords * 5000));
                }
                
                // Penalización por longitud excesiva (preferir opciones más específicas)
                const totalLength = searchFields.reduce((len, field) => {
                    const fieldValue = typeof field === 'function' ? field(option) : option[field];
                    return len + (fieldValue ? fieldValue.length : 0);
                }, 0);
                
                if (totalLength > 80) {
                    score = Math.max(0, score - (totalLength - 80) * 2);
                }
                
                return { 
                    option, 
                    score, 
                    exactPhraseMatches, 
                    sequentialMatches, 
                    wordMatches,
                    allWordsFound,
                    startsWithInput 
                };
            })
            .filter(item => {
                //Para busquedas cortas (≤3 letras), ser mas estricto
                if (normalizedInput.length <= 3) {
                    return item.score > 10000;
                }
                return item.score > 0;
            })
            .sort((a, b) => {
                if (normalizedInput.length <= 3 && a.startsWithInput !== b.startsWithInput) {
                    return b.startsWithInput - a.startsWithInput;
                }

                // 1. Priorizar coincidencias de frase exacta
                if (a.exactPhraseMatches !== b.exactPhraseMatches) {
                    return b.exactPhraseMatches - a.exactPhraseMatches;
                }
                
                // 2. Priorizar coincidencias secuenciales
                if (a.sequentialMatches !== b.sequentialMatches) {
                    return b.sequentialMatches - a.sequentialMatches;
                }
                
                // 3. Priorizar cuando todas las palabras fueron encontradas
                if (a.allWordsFound !== b.allWordsFound) {
                    return b.allWordsFound - a.allWordsFound;
                }
                
                // 4. Priorizar por score total
                if (b.score !== a.score) {
                    return b.score - a.score;
                }
                
                // 5. En caso de empate total, preferir textos más cortos (más específicos)
                const aLength = searchFields.reduce((len, field) => {
                    const fieldValue = typeof field === 'function' ? field(a.option) : a.option[field];
                    return len + (fieldValue ? fieldValue.length : 0);
                }, 0);
                
                const bLength = searchFields.reduce((len, field) => {
                    const fieldValue = typeof field === 'function' ? field(b.option) : b.option[field];
                    return len + (fieldValue ? fieldValue.length : 0);
                }, 0);
                
                return aLength - bLength;
            })
            // .slice(0, maxResults)
            .map(item => item.option);
            
        return scoredOptions;
    };
};



function GestionCotizaciones({ cotizacion, detalles, modo, onClose }) {
    const { tienePermiso } = useAccesos();
    const server = process.env.REACT_APP_API_ROUTE;
    const key = process.env.REACT_APP_API_KEY;
    const { sucursalSeleccionada } = useSucursal();
    const [clientes, setClientes] = useState([]);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(false);
    const [sucursales, setSucursales] = useState([]);
    const [origenes, setOrigenes] = useState([]);
    const [mediosPago, setMediosPago] = useState([]);
    const [Medicos, setMedicos] = useState([]);
    const [sucursalEntrega, setSucursalEntrega] = useState('');
    const [IsEditing, setIsEditing] = useState(false);
    const [identidad, setIdentidad] = useState('');
    const [edad, setEdad] = useState('');
    const [sexo, setSexo] = useState('');
    const [direccion, setDireccion] = useState('');
    const [fechaCotizacion, setFechaCotizacion] = useState('');
    // Función para calcular días restantes hasta fin de mes
    const calcularDiasHastaFinDeMes = () => {
        const hoy = new Date();
        const ultimoDiaMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
        return ultimoDiaMes.getDate() - hoy.getDate() + 1; // +1 para incluir el día actual
    };
    
    const [validez, setValidez] = useState(calcularDiasHastaFinDeMes());
    const [medidaT, setMedidaT] = useState('');
    const [telefono, setTelefono] = useState('');
    const [correoElectronico, setCorreoElectronico] = useState('');
    const { user } = useUser();
    const [perfil, setPerfil] = useState('');
    const [medicoSeleccionado, setMedicoSeleccionado] = useState('');
    const [origen, setOrigen] = useState('');
    const [medioPago, setMedioPago] = useState('');
    const [tipoPago, setTipoPago] = useState('');
    const [observaciones, setObservaciones] = useState('');

    const [tipoCotizacion, setTipoCotizacion] = useState('');
    const [horaRecepcion, setHoraRecepcion] = useState('');
    const [horaEntrega, setHoraEntrega] = useState('');
    const [whatsapp, setWhatsapp] = useState(false);
    const [app, setApp] = useState(false);
    const [correo, setCorreo] = useState(false);
    const [impresos, setImpresos] = useState(false);

    const [analisis, setAnalisis] = useState([]);
    const [analisisPrecios, setAnalisisPrecios] = useState([]);

    const [tiposPagos, setTiposPagos] = useState([]);
    const [tiposCotizacion, setTiposCotizacion] = useState([]);
    const [tiposPrecio, setTiposPrecio] = useState([]);
    const [prioridadDescuento, setPrioridadDescuento] = useState([]);
    // Cambiado de descuentoAplicado a descuentosAplicados
    const [descuentosAplicados, setDescuentosAplicados] = useState([]);
    const [medidasTiempo, setMedidasTiempo] = useState([]);
    const [polizas, setPolizas] = useState([]);
    const [perfiles, setPerfiles] = useState([]);
    const [sexos, setSexos] = useState([]);
    const [tipoPrecio, setTipoPrecio] = useState('');
    const [valor, setValor] = useState('');
    const [cantidad, setCantidad] = useState(1);
    const [descuento, setDescuento] = useState('');
    const [descuentoTotalP, setDescuentoTotalP] = useState('');
    const [descuentoTotal, setDescuentoTotal] = useState('');
    const [totalPagar, setTotalPagar] = useState('');
    const [valorVenta, setValorVenta] = useState('');
    const [cantidadTotal, setCantidadTotal] = useState('');

    const [elegible, setElegible] = useState(false);
    const [comentarios, setComentarios] = useState('');

    const [analisisAgregados, setAnalisisAgregados] = useState([]);

    const [analisisSeleccionado, setAnalisisSeleccionado] = useState(null);

    const [searchQuery, setSearchQuery] = useState(''); // Query actual para la búsqueda
    const [inputValue, setInputValue] = useState(''); // Texto que se muestra en el input
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [totalClientes, setTotalClientes] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [tablePage, setTablePage] = useState(0); // Estado para la página actual de la tabla
    const [rowsPerPage, setRowsPerPage] = useState(50); // Estado para el número de filas por página
    const [openModalCliente, setOpenModalCliente] = useState(false); // Controla la apertura del modal

    const [isAseguradora, setIsAseguradora] = useState(false);
    const [descuentoGlobal, setDescuentoGlobal] = useState(0);
    const [analisisPerfilActual, setAnalisisPerfilActual] = useState([]);
    // Variables para la sección Datos del Paciente Asegurado
    const [poliza, setPoliza] = useState('');
    const [empresa, setEmpresa] = useState('');
    const [nTransaccion, setNTransaccion] = useState('');
    const [minimo, setMinimo] = useState('');
    const [maximo, setMaximo] = useState('');
    const [nVerificacion, setNVerificacion] = useState('');
    const [copago, setCopago] = useState('');
    const [autorizado, setAutorizado] = useState('');
    const [deducible, setDeducible] = useState('');
    const [valorElegible, setValorElegible] = useState('');
    const [noElegibles, setNoElegibles] = useState('');
    const [aseguradora, setAseguradora] = useState('');
    const [valorCopago, setValorCopago] = useState('');    const [totalAsegurado, setTotalAsegurado] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [apiResult, setApiResult] = useState({ message: '', status: '' });
    const [idCotizacion, setIdCotizacion] = useState(null); // Estado para almacenar el ID de la cotización
    
    // Estados para gestionar el modal de orden de trabajo
    const [openOrdenTrabajoModal, setOpenOrdenTrabajoModal] = useState(false);
    const [ordenTrabajoData, setOrdenTrabajoData] = useState(null);
    
    // Estados para el modal de vista previa PDF
    const [openReimprimirPreview, setOpenReimprimirPreview] = useState(false);
    const [reimprimirPdfUrl, setReimprimirPdfUrl] = useState(null);
    const [reimprimirLoading, setReimprimirLoading] = useState(false);
    const [reimprimirError, setReimprimirError] = useState(null);
    // Estados para el modal
    const [openModalCondiciones, setOpenModalCondiciones] = useState(false);
    const [selectedAnalisis, setSelectedAnalisis] = useState(null);
    const [isGuardando, setIsGuardando] = useState(false);
    const refAutocompleteAnalisis = useRef();
    const enterLockRef = useRef(false);
    const analisisEnProcesoRef = useRef(new Set());
    const handleVerCondiciones = (analisis) => {
        setSelectedAnalisis(analisis);
        setOpenModalCondiciones(true);
    };

    const [perfilDescuento, setPerfilDescuento] = useState(null);
    const [descuentoGlobalRemitido, setDescuentoGlobalRemitido] = useState(0);
    const [descuentoAsegurado, setDescuentoAsegurado] = useState('');
    const [puedeCambiarDescuento, setPuedeCambiarDescuento] = useState(false);
    const [ultimoValorAutorizado, setUltimoValorAutorizado] = useState(null);
    const [tiposPrecioAnalisis, setTiposPrecioAnalisis] = useState([]);
    const [inputAnalisisValue, setInputAnalisisValue] = useState('');
    
    useEffect(() => {
        setPuedeCambiarDescuento(tienePermiso('Aplicar Descuento en Cotización'));
    }, []);

    const [continuarDespuesDeModal, setContinuarDespuesDeModal] = useState(false); const [isAdding, setIsAdding] = useState(false);
    const [searchAnalisisQuery, setSearchAnalisisQuery] = useState('');
    const [lastInput, setLastInput] = useState("");

    const descuentoManualRef = useRef(false);

    const [analisisListo, setAnalisisListo] = useState(false);

    const [canalesVenta, setCanalesVenta] = useState([]);
    const [canalVentaSeleccionado, setCanalVentaSeleccionado] = useState('');




        // === Recalculo dinámico de descuentos según condiciones actuales ===
        const esPerfilCompleto = (descuentoPerfil, listaAnalisis) => {
            if (!descuentoPerfil || !descuentoPerfil.idsAnalisisPerfil) return false;
            return descuentoPerfil.idsAnalisisPerfil.every(id => listaAnalisis.some(a => a.idTipoAnalisis === id));
        };
        // Construye una firma única de las condiciones de descuento actuales.
        const buildCondicionesFirma = () => {
            return [
                clienteSeleccionado?.idCliente || '',
                origen?.idOrigen || '',
                medicoSeleccionado?.idMedico || '',
                medioPago?.id || '',
                tipoPago || '',
                isAseguradora ? '1' : '0',
                perfilDescuento?.idPerfil || '',
                descuentoGlobal,
                descuentoGlobalRemitido
            ].join('|');
        };
    
        const recalcularDescuentosCondiciones = async () => {
            const firmaActual = buildCondicionesFirma();
            let perfilesValidos = [];
            let huboCambioPerfiles = false;
            descuentosAplicados.forEach(d => {
                if (d.tipo === 'Perfil') {
                    if (esPerfilCompleto(d, analisisAgregados)) {
                        perfilesValidos.push(d);
                    } else {
                        huboCambioPerfiles = true;
                    }
                } else {
                    perfilesValidos.push(d);
                }
            });
            if (huboCambioPerfiles) setDescuentosAplicados(perfilesValidos);
    
            const nuevosAnalisis = await Promise.all(analisisAgregados.map(async a => {
                // Si el registro está facturado no se recalcula ni modifica.
               
                 // 🔐 1️⃣ NUNCA tocar descuentos manuales
                // if (a.descuentoManual) {
                //     return a;
                // }
                if (a.descuentoManual === true) {
                    const porcentaje = a.porcentajeManual ?? 0;

                    const descuentoMonto = (parseFloat(a.precio) * porcentaje) / 100;
                    const total = (parseFloat(a.precio) - descuentoMonto) * parseInt(a.cantidad);

                    return {
                        ...a,
                        descuento: descuentoMonto.toFixed(2),
                        total: total.toFixed(2),
                        firmaCondiciones: firmaActual
                    };
}

               
                if (a.descuentoDelBackend) {
                    console.log('⏸️ Conservando descuento del backend para:', a.analisis, '- Descuento:', a.descuento);
                    // Asegurar que el flag se mantiene incluso después de recálculos
                    return { ...a, descuentoDelBackend: true };
                }

                if (modo === 'modificar' && a.facturado) {
                    return a;
                }
                // Si estamos en modo modificar y la firma no cambió, conservar valores actuales.
                if (modo === 'modificar' && a.firmaCondiciones && a.firmaCondiciones === firmaActual) {
                    return a;
                }
                let porcentaje = 0;
                if (a.remitido && descuentoGlobalRemitido > 0) {
                    porcentaje = descuentoGlobalRemitido;
                } else if (descuentoGlobal > 0) {
                    porcentaje = descuentoGlobal;
                } else {
                    if (a.idPerfil) {
                        const descPerfil = perfilesValidos.find(d => d.tipo === 'Perfil' && d.idPerfil === a.idPerfil);
                        if (descPerfil && esPerfilCompleto(descPerfil, analisisAgregados)) {
                            porcentaje = descPerfil.porcentaje || 0;
                        }
                    }
                    if (porcentaje === 0) {
                        const descAnalisis = await obtenerDescuentoPorAnalisis(a.idTipoAnalisis);
                        porcentaje = parseFloat(descAnalisis?.porcentaje) || 0;
                    }
                }
                const descuentoMonto = (parseFloat(a.precio) * porcentaje) / 100;
                const total = (parseFloat(a.precio) - descuentoMonto) * parseInt(a.cantidad);
                return { ...a, descuento: descuentoMonto.toFixed(2), total: total.toFixed(2), firmaCondiciones: firmaActual,  descuentoDelBackend: a.descuentoDelBackend || false //🔴 Preservar flag si ya existe
                };
            }));
            const mismos = analisisAgregados.length === nuevosAnalisis.length && analisisAgregados.every((oldA, idx) => oldA.descuento === nuevosAnalisis[idx].descuento && oldA.total === nuevosAnalisis[idx].total);
            if (!mismos) {
                setAnalisisAgregados(nuevosAnalisis);
                calcularTotales(nuevosAnalisis);
                if (isPolizaValidForOrigen(clienteSeleccionado?.idPoliza, polizas)) {
                    recalcularElegiblesNoElegibles(nuevosAnalisis);
                }
            }
        };

            useEffect(() => {
                // 🔴 PROTECCIÓN CRÍTICA: En modo modificar con datos del backend, NO HACER NADA
                if (modo === 'modificar' && analisisAgregados.length > 0) {
                    const todosDelBackend = analisisAgregados.every(a => a.descuentoDelBackend === true);
                    console.log('🔍 CHECK useEffect:', {
                        modo,
                        cantidadAnalisis: analisisAgregados.length,
                        todosDelBackend,
                        flags: analisisAgregados.map(a => ({ analisis: a.analisis, descuentoDelBackend: a.descuentoDelBackend }))
                    });
                    
                    if (todosDelBackend) {
                        console.log('🛡️ PROTECCIÓN ACTIVA: Modo modificar + Backend data - BLOQUEADO recalcularDescuentosCondiciones');
                        return;
                    }
                }
                
                if (analisisAgregados.length === 0) return;
                
                console.log('🔄 Ejecutando recalcularDescuentosCondiciones - Hay análisis nuevos en modo crear');
                recalcularDescuentosCondiciones();
            }, [clienteSeleccionado, origen, medicoSeleccionado, medioPago, tipoPago, isAseguradora, perfilDescuento, analisisAgregados, descuentoGlobal, descuentoGlobalRemitido]);
        
    
        // useEffect(() => {
        //     if (analisisAgregados.length === 0) return;
        //     recalcularDescuentosCondiciones();
        // }, [clienteSeleccionado, origen, medicoSeleccionado, medioPago, tipoPago, isAseguradora, perfilDescuento, analisisAgregados, descuentoGlobal, descuentoGlobalRemitido]);
    
        useEffect(() => {
            if (analisisAgregados.length === 0) return;

            const hayAutomaticos = analisisAgregados.some(a => !a.descuentoManual);
               if (!hayAutomaticos) return;

            recalcularDescuentosCondiciones();
        }, [clienteSeleccionado, origen, medicoSeleccionado, medioPago, tipoPago, isAseguradora, perfilDescuento, descuentoGlobal, descuentoGlobalRemitido]);
    

         useEffect(() => {
                // ⛔ Si la orden YA trae médico, NO sobrescribirlo
                if (cotizacion?.idMedico) return;
        
                if (Medicos.length > 0) {
                    const medicoPorDefecto = Medicos.find(m => m.idMedico === 2563);
                    if (medicoPorDefecto) {
                        setMedicoSeleccionado(medicoPorDefecto);
                    }
                }
            }, [Medicos]);
        
            useEffect(() => {
                 // ⛔ Si la orden YA trae origen, NO sobrescribirlo
                if (cotizacion?.idOrigen) return;
        
                if (origenes.length > 0) {
                    const origenPorDefecto = origenes.find(m => m.idOrigen === 0);
                    if (origenPorDefecto) {
                        setOrigen(origenPorDefecto);
                    }
                }
            }, [origenes]);


        useEffect(() => {
        if (!cotizacion) return;
    
        const medicoCotizacion = Medicos.find(m => m.idMedico === cotizacion.idMedico);
        if (medicoCotizacion) setMedicoSeleccionado(medicoCotizacion);
    
        const origenCotizacion = origenes.find(o => o.idOrigen === cotizacion.idOrigen);
        if (origenCotizacion) setOrigen(origenCotizacion);
    
        }, [cotizacion, Medicos, origenes]);

    const debouncedFetchClientes = useMemo(
        () =>
            debounce((searchText) => {
                fetchClientes(searchText);
            }, 500),
        []
    );

      const analisisFiltrados = analisisAgregados.filter(a => {
        if (!searchAnalisisQuery) return true;
        
        const normalizedQuery = normalizeText(searchAnalisisQuery);
        const normalizedAnalisis = normalizeText(a.analisis);
        
        return normalizedAnalisis.includes(normalizedQuery);
    });

    // Manejar el cambio en el input del Autocomplete
    const handleInputChange = (event, newInputValue) => {
        setInputValue(newInputValue);
        debouncedFetchClientes(newInputValue);
    };

    // Nueva función para manejar el cambio de tipo de precio
    const handleTipoPrecioChange = async (e) => {
        const nuevoTipoPrecio = e.target.value;
        console.log('💰 [DEBUG] handleTipoPrecioChange - nuevoTipoPrecio:', nuevoTipoPrecio);
        setTipoPrecio(nuevoTipoPrecio);

        if (!analisisSeleccionado || !nuevoTipoPrecio) {
            console.log('⚠️ [DEBUG] No hay análisis o tipo de precio, limpiando valor');
            setValor('');
            return;
        }

        // Buscar el precio en los tipos de precio disponibles para este análisis
        const precioSeleccionado = tiposPrecioAnalisis.find(p => p.idTipoPrecio === parseInt(nuevoTipoPrecio));
        console.log('🔍 [DEBUG] Precio seleccionado encontrado:', precioSeleccionado);
        
        if (precioSeleccionado) {
            setValor(precioSeleccionado.precio);
            console.log('✅ [DEBUG] Valor actualizado a:', precioSeleccionado.precio);
        } else {
            console.log('❌ [DEBUG] No se encontró precio para el tipo seleccionado');
            setValor('');
        }
    };

        const obtenerDatosClasificacionPoliza = async (idPoliza1, idClasificacionProducto) => {
        try {
            const res = await fetch(`${server}/MgPolizasDeSeguroRangos/bypoliza/${idPoliza1}`);
            const data = await res.json();
            const rangos = data.$values || [];
            console.log(rangos);
            const rango = rangos.find(r => r.idClasificacionProducto === idClasificacionProducto);

            if (rango) {
                // Seteas aquí los valores si tenés estados definidos
                setCopago(rango.porcentajeCopago);
                setMinimo(rango.valorMinimo);
                setMaximo(rango.valorMaximo);

                // También podés retornarlo si preferís manejarlo desde donde lo llamás
                return {
                    porcentajeCopago: rango.porcentajeCopago,
                    valorMinimo: rango.valorMinimo,
                    valorMaximo: rango.valorMaximo
                };
            } else {
                // Si no se encuentra la clasificación
                setCopago(0);
                setMinimo(0);
                setMaximo(0);
                return null;
            }
        } catch (error) {
            console.error("Error al obtener el rango por clasificación:", error);
            return null;
        }
    };


    const obtenerDescuentoPorOrigen = async (idOrigen) => {
        try {
            const response = await fetch(`${server}/mg_origen_empresa_descuento_por_uso/byidorigen/${idOrigen}`);
            const data = await response.json();
            const descuento = data.porcentajeDescuento || 0;
            return descuento;
        } catch (error) {
            console.error('Error al obtener el descuento por origen:', error);
            return 0;
        }
    };

    const obtenerDescuentoPorTarjeta = async (idMedioPago) => {

        try {
            const fechaActual = new Date();
            const añoActual = fechaActual.getFullYear();

            //Todo: Cambiar fecha de cotización por fecha actual
            const fechaDesde = `${añoActual}-01-01`;
            const fechaHasta = `${añoActual}-12-31`;

            const response = await fetch(`${server}/PrPromociones/byidtipopromocion/3`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    search: '',
                    desde: fechaDesde,
                    hasta: fechaHasta
                })
            });

            const data = await response.json();
            const promociones = data.$values || [];
            console.log(promociones);

            // Filtrar promociones activas según la fecha de cotización
            const promocionesActivas = promociones.filter(promo => {
                const fechaDesdePromo = new Date(promo.fechaDesde);
                const fechaHastaPromo = new Date(promo.fechaHasta);
                const fechaCot = new Date(fechaCotizacion);

                return fechaCot >= fechaDesdePromo && fechaCot <= fechaHastaPromo;
            });

            let maxDescuento = 0;

            for (const promo of promocionesActivas) {
                const responseDetalles = await fetch(`${server}/PrDetallesDeDescuentosXTarjetasBancarias/byIdPromocion/${promo.id}`);
                const dataDetalles = await responseDetalles.json();
                const detalles = dataDetalles.$values || [];

                // Buscar si el perfil seleccionado está en los detalles
                const detalleMedioP = detalles.find(detalle => detalle.idTarjeta === idMedioPago);

                if (detalleMedioP && detalleMedioP.porcentaje > maxDescuento) {
                    maxDescuento = detalleMedioP.porcentaje;
                }
            }

            return maxDescuento;
        } catch (error) {
            console.error('Error al obtener el descuento por medio de pago:', error);
            return 0;
        }
    };


    const obtenerDescuentoPorPerfil = async (idPerfil) => {
        try {
            const fechaActual = new Date();
            const añoActual = fechaActual.getFullYear();

            const fechaDesde = `${añoActual}-01-01`;
            const fechaHasta = `${añoActual}-12-31`;

            const response = await fetch(`${server}/PrPromociones/byidtipopromocion/2`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    search: '',
                    desde: fechaDesde,
                    hasta: fechaHasta
                })
            });

            const data = await response.json();
            const promociones = data.$values || [];
            console.log(promociones);

            // Filtrar promociones activas según la fecha de cotización
            const promocionesActivas = promociones.filter(promo => {
                const fechaDesdePromo = new Date(promo.fechaDesde);
                const fechaHastaPromo = new Date(promo.fechaHasta);
                const fechaCot = new Date(fechaCotizacion);

                return fechaCot >= fechaDesdePromo && fechaCot <= fechaHastaPromo;
            });

            let maxDescuento = 0;

            for (const promo of promocionesActivas) {
                const responseDetalles = await fetch(`${server}/PrDetallesDeDescuentosXPerfilDeAnalisis/byidpromocion/${promo.id}`);
                const dataDetalles = await responseDetalles.json();
                const detalles = dataDetalles.$values || [];

                // Buscar si el perfil seleccionado está en los detalles
                const detallePerfil = detalles.find(detalle => detalle.idPerfil === idPerfil);

                if (detallePerfil && detallePerfil.porcentaje > maxDescuento) {
                    maxDescuento = detallePerfil.porcentaje;
                }
            }

            return maxDescuento;
        } catch (error) {
            console.error('Error al obtener el descuento por perfil:', error);
            return 0;
        }
    };



    const obtenerDescuentoPorAnalisis = async (idTipoAnalisis) => {
        // try {
        //     const fechaActual = new Date();
        //     const añoActual = fechaActual.getFullYear();

        //     const fechaDesde = `${añoActual}-01-01`;
        //     const fechaHasta = `${añoActual}-12-31`;

        //     const response = await fetch(`${server}/PrPromociones/byidtipopromocion/1`, {
        //         method: 'POST',
        //         headers: { 'Content-Type': 'application/json' },
        //         body: JSON.stringify({
        //             search: '',
        //             desde: fechaDesde,
        //             hasta: fechaHasta
        //         })
        //     });

        //     const data = await response.json();
        //     const promociones = data.$values || [];

        //     // Filtrar promociones activas según la fecha de cotización
        //     const promocionesActivas = promociones.filter(promo => {
        //         const fechaDesdePromo = new Date(promo.fechaDesde);
        //         const fechaHastaPromo = new Date(promo.fechaHasta);
        //         const fechaCot = new Date(fechaCotizacion);

        //         return fechaCot >= fechaDesdePromo && fechaCot <= fechaHastaPromo;
        //     });

        //     console.log(promocionesActivas);

        //     let maxDescuento = 0;

        //     for (const promo of promocionesActivas) {
        //         const responseDetalles = await fetch(`${server}/PrPromocionesDetallesDeDescuentoes/byidpromocion/${promo.id}`);
        //         const dataDetalles = await responseDetalles.json();
        //         const detalles = dataDetalles.$values || [];

        //         // Buscar si el análisis está en los detalles
        //         const detalleAnalisis = detalles.find(detalle => detalle.idTipoAnalisis === idTipoAnalisis);

        //         if (detalleAnalisis && detalleAnalisis.porcentaje > maxDescuento) {
        //             maxDescuento = detalleAnalisis.porcentaje;
        //         }
        //     }

        //     return maxDescuento;
        // } catch (error) {
        //     console.error('Error al obtener el descuento por análisis:', error);
        //     return 0;
        // }

        try {
            // Si recibimos un objeto, extraemos el ID
            const analisisId = typeof idTipoAnalisis === 'object' ? idTipoAnalisis.idTipoAnalisis : idTipoAnalisis;

            const response = await fetch(`${server}/PrPromocionesDetallesDeDescuentoes/byidtipoAnalisis/${analisisId}/${sucursalSeleccionada.idSucursal}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            
            if (!response.ok) {
                if (response.status === 404) {
                    // Si no se encuentra, retornamos objeto vacío en lugar de error
                    return {};
                }
                throw new Error('Error obteniendo descuento');
            }
            
            const data = await response.json();
            
            // La API ahora devuelve un objeto directo, no un array con $values
            // Verificamos que data tenga las propiedades que necesitamos
            if (data && data.porcentaje !== undefined) {
                return {
                    idPromocion: data.idPromocion,
                    porcentaje: data.porcentaje,
                    descuentoRemitido: data.descuentoRemitido // Nueva propiedad
                };
            }
            
            // Si no hay datos válidos
            return {};
        } catch (error) {
            console.error('Error al obtener el descuento por análisis:', error);
            return {};
        }
    };



    const calcularDescuento = async () => {
        let descuentos = [];

        // Descuento por origen
        if (origen) {
            const descuentoOrigen = await obtenerDescuentoPorOrigen(origen.idOrigen);
            const prioridad = prioridadDescuento.find(p => p.id === 2);
            descuentos.push({
                tipo: 'Origen',
                porcentaje: descuentoOrigen,
                orden: prioridad ? prioridad.orden : Infinity
            });
        }

        // Descuento por médico
        if (medicoSeleccionado && medicoSeleccionado.porcentajeDescuento) {
            const prioridad = prioridadDescuento.find(p => p.id === 3);
            descuentos.push({
                tipo: 'Médico',
                porcentaje: medicoSeleccionado.porcentajeDescuento,
                orden: prioridad ? prioridad.orden : Infinity
            });
        }

        // Descuento por tercera edad
        if (clienteSeleccionado && clienteSeleccionado.fechaNacimiento && esTerceraEdad(clienteSeleccionado.fechaNacimiento)) {
            const prioridad = prioridadDescuento.find(p => p.id === 1);
            descuentos.push({
                tipo: 'Tercera Edad',
                porcentaje: 30, // 30% de descuento
                orden: prioridad ? prioridad.orden : Infinity
            });
        }

        // Descuento por medio de pago
        if (medioPago) {
            const descuentoMedioPago = await obtenerDescuentoPorTarjeta(medioPago.id);
            const prioridad = prioridadDescuento.find(p => p.id === 7);
            descuentos.push({
                tipo: 'MedioPago',
                porcentaje: descuentoMedioPago,
                orden: prioridad ? prioridad.orden : Infinity
            });
        }

        // Descuento por perfil
        descuentosAplicados.forEach((descuentoPerfil) => {
            const prioridad = prioridadDescuento.find((p) => p.id === 4);
            descuentos.push({
                ...descuentoPerfil,
                orden: prioridad ? prioridad.orden : Infinity,
            });
        });

        // Ordenar descuentos por prioridad
        descuentos.sort((a, b) => a.orden - b.orden);

        let descuentoAplicar = descuentos[0] || null;

        // Si el descuento global es 0, aplicar el descuento por perfil o análisis
        if (descuentoAplicar && descuentoAplicar.porcentaje === 0) {
            if (descuentoAplicar.tipo === 'Perfil') {
                setDescuentoGlobal(0);
                setDescuento(descuentoAplicar.porcentaje);
                aplicarDescuentosPerfiles();
            } else {
                setDescuentoGlobal(0);
                setDescuento(0);
                aplicarDescuentosPerfiles();
            }
        } else if (descuentoAplicar) {
            // Aplicar descuento global
            setDescuentoGlobal(descuentoAplicar.porcentaje);
            setDescuento(descuentoAplicar.porcentaje);
            aplicarDescuentoGlobalAnalisisAgregados(descuentoAplicar.porcentaje);
        } else {
            setDescuentoGlobal(0);
            setDescuento(0);
            recalcularDescuentosAnalisisAgregados(analisisAgregados);
        }
    };



    const esTerceraEdad = (fechaNacimiento) => {
        console.log(fechaNacimiento);
        const hoy = new Date();
        const fechaNac = new Date(fechaNacimiento);
        let edad = hoy.getFullYear() - fechaNac.getFullYear();
        const m = hoy.getMonth() - fechaNac.getMonth();
        console.log(m);
        if (m < 0 || (m === 0 && hoy.getDate() < fechaNac.getDate())) {
            edad--;
        }
        console.log(edad);
        return edad >= 60;
    };


    const aplicarDescuentoGlobalAnalisisAgregados = (porcentajeDescuento) => {
        const nuevosAnalisisAgregados = analisisAgregados.map((analisis) => {
            const descuento = (analisis.precio * porcentajeDescuento) / 100;
            const total = (analisis.precio - descuento) * analisis.cantidad;

            return {
                ...analisis,
                descuento: descuento.toFixed(2),
                total: total.toFixed(2)
            };
        });

        setAnalisisAgregados(nuevosAnalisisAgregados);
        calcularTotales(nuevosAnalisisAgregados);
    };

    const aplicarDescuentosPerfiles = async () => {
        const nuevosAnalisisAgregados = await Promise.all(
            analisisAgregados.map(async (analisis) => {
                // Encontrar si el análisis pertenece a un perfil con descuento
                const descuentoPerfil = descuentosAplicados.find(
                    (descuento) =>
                        descuento.tipo === 'Perfil' && descuento.idPerfil === analisis.idPerfil
                );

                let porcentajeDescuento = 0;
                if (descuentoPerfil) {
                    porcentajeDescuento = descuentoPerfil.porcentaje;
                } else {
                    // Aplicar descuento por análisis si está disponible
                    porcentajeDescuento = await obtenerDescuentoPorAnalisis(analisis.idTipoAnalisis);
                }

                const descuentoMonto = (analisis.precio * porcentajeDescuento) / 100;
                const total = (analisis.precio - descuentoMonto) * analisis.cantidad;

                return {
                    ...analisis,
                    descuento: descuentoMonto.toFixed(2),
                    total: total.toFixed(2),
                };
            })
        );

        setAnalisisAgregados(nuevosAnalisisAgregados);
        calcularTotales(nuevosAnalisisAgregados);
    };


    const recalcularDescuentosAnalisisAgregados = async (analisisArray) => {
        const nuevosAnalisisAgregados = await Promise.all(
            analisisArray.map(async (analisis) => {
                let porcentajeDescuento = 0;

                if (descuentoGlobal > 0) {
                    porcentajeDescuento = descuentoGlobal;
                } else {
                    // Verificar si el análisis pertenece a un perfil completo
                    const descuentoPerfil = descuentosAplicados.find(
                        (descuento) =>
                            descuento.tipo === 'Perfil' && descuento.idPerfil === analisis.idPerfil
                    );

                    // Verificar si el perfil está incompleto
                    const perfilCompleto = descuentoPerfil && descuentoPerfil.idsAnalisisPerfil.every((idAnalisis) =>
                        analisisArray.some(a => a.idTipoAnalisis === idAnalisis)
                    );

                    if (perfilCompleto) {
                        porcentajeDescuento = descuentoPerfil.porcentaje;
                    } else {
                        // Aplicar descuento por análisis si no hay perfil completo
                        porcentajeDescuento = await obtenerDescuentoPorAnalisis(analisis.idTipoAnalisis) || 0;
                    }
                }

                const descuentoMonto = (analisis.precio * porcentajeDescuento) / 100;
                const total = (analisis.precio - descuentoMonto) * analisis.cantidad;

                return {
                    ...analisis,
                    descuento: descuentoMonto.toFixed(2),
                    total: total.toFixed(2),
                };
            })
        );

        setAnalisisAgregados(nuevosAnalisisAgregados);
        calcularTotales(nuevosAnalisisAgregados);
    };


    const aplicarDescuentoAnalisisAgregados = (porcentajeDescuento) => {
        const nuevosAnalisisAgregados = analisisAgregados.map((analisis) => {
            const descuento = (analisis.precio * porcentajeDescuento) / 100;
            const total = (analisis.precio - descuento) * analisis.cantidad;

            return {
                ...analisis,
                descuento: descuento.toFixed(2),
                total: total.toFixed(2)
            };
        });

        setAnalisisAgregados(nuevosAnalisisAgregados);
        calcularTotales(nuevosAnalisisAgregados);
    };


    // Descomentar si se quiere volver al cálculo automático de descuentos
    // useEffect(() => {
    //     calcularDescuento();
    // }, [medicoSeleccionado, origen, clienteSeleccionado, perfil, analisisSeleccionado, fechaCotizacion, medioPago]);


    const fetchClientes = async (search = '') => {
        try {
            setIsLoading(true);

            const response = await fetch(`${server}/MgClientes/buscador/${search}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            const data = await response.json();

            if (data && data.clientes && Array.isArray(data.clientes.$values)) {
                const clientesData = data.clientes.$values;

                setClientes(clientesData);
                setTotalClientes(data.total);
                setHasMore(clientesData.length >= limit);
            } else {
                setClientes([]);
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error al cargar los clientes:', error);
        } finally {
            setIsLoading(false);
        }
    };


    useEffect(() => {

    const cargarPoliza = async () => {

        if (!origen || !clienteSeleccionado) return;

        const idPoliza =
            clienteSeleccionado.idPoliza ??
            clienteSeleccionado.IdPoliza ??
            0;

        const tieneAseguradora =
            origen?.aseguradora === true ||
            origen?.aseguradora === 1;

        console.log("🟢 VALIDANDO useEffect POLIZA:", {
            origen,
            clienteSeleccionado,
            idPoliza,
            tieneAseguradora
        });

        if (!tieneAseguradora || idPoliza <= 0) return;

        try {

            const response = await fetch(
                `${server}/MgPolizasDeSeguroes/byidorigen/${origen.idOrigen}`
            );

            const data = await response.json();

            const polizaEncontrada = data.$values.find(
                (p) => p.id === idPoliza
            );

            console.log("✅ POLIZA ENCONTRADA:", polizaEncontrada);

            if (polizaEncontrada) {

                await obtenerDatosClasificacionPoliza(idPoliza, 2);

                setPoliza(polizaEncontrada.codigo || '');
                setEmpresa(polizaEncontrada.origen || '');

                setIsAseguradora(true);
            }

        } catch (error) {

            console.error("❌ Error cargando póliza:", error);

        }
    };

    cargarPoliza();

}, [origen, clienteSeleccionado]);

    const handleOrigenChange = async (e, newValue) => {
        setOrigen(newValue);
        if (newValue && newValue.aseguradora && clienteSeleccionado && clienteSeleccionado.idPoliza) {
            setIsAseguradora(true);
            console.log(clienteSeleccionado);
            // Validar que idPoliza no sea cero
            if (clienteSeleccionado && clienteSeleccionado.idPoliza !== 0) {
                console.log('entro al if');
                try {
                    const response = await fetch(`${server}/MgPolizasDeSeguroes/byidorigen/${newValue.idOrigen}`);
                    const data = await response.json();

                    // const codigoPoliza = polizas.find((p) => p.idPoliza === clienteSeleccionado.idPoliza);
                    // Busca el idPoliza en el resultado
                    const poliza = data.$values.find((p) => p.id === clienteSeleccionado.idPoliza);

                    if (poliza) {
                        setPoliza(poliza.codigo);
                        setMinimo(poliza.valorMinimo);
                        setMaximo(poliza.valorMaximo);
                        setCopago(poliza.porcentajeCopago);
                    } else {
                        setPoliza(null);
                        setMinimo(0);
                        setMaximo(0);
                        setCopago(0);
                    }

                    // Recalcular elegible y no elegible con los análisis actuales
                    recalcularElegiblesNoElegibles(analisisAgregados);
                } catch (error) {
                    console.error("Error al obtener los detalles de la póliza:", error);
                }
            }
        } else {
            setIsAseguradora(false);
        }
    };

    // función para verificar si la póliza del cliente se encuentra entre las pólizas del origen
    const isPolizaValidForOrigen = (clientePoliza, origenPolizas) => {
        return clientePoliza && origenPolizas.some(poliza => poliza.id === clientePoliza);
    };


    // Función para calcular los valores elegibles y no elegibles
    const recalcularElegiblesNoElegibles = (analisis) => {


        // Si el cliente no tiene póliza o no coincide con el origen, no se calculan los valores
        if (!isPolizaValidForOrigen(clienteSeleccionado.idPoliza, polizas)) {
            setValorElegible(0);
            setNoElegibles(0);
            setValorCopago(0);
            setTotalAsegurado(0);
            return;
        }


        const totalElegible = analisis
            .filter((a) => a.elegible)
            .reduce((acc, curr) => acc + parseFloat(curr.total), 0);

        const totalNoElegible = analisis
            .filter((a) => !a.elegible)
            .reduce((acc, curr) => acc + parseFloat(curr.total), 0);


        // Calcular porcentaje de aseguradora y copago basado en el porcentaje de copago
        const porcentajeAseguradora = 100 - copago;
        const valorAseguradora = (porcentajeAseguradora / 100) * totalElegible;
        const valorCopago = (copago / 100) * totalElegible;



        // Setear los valores calculados
        setValorElegible(totalElegible.toFixed(2));
        setNoElegibles(totalNoElegible.toFixed(2));
        setAseguradora(valorAseguradora.toFixed(2));
        setValorCopago(valorCopago.toFixed(2));

        // Calcular y setear el total asegurado
        const totalAsegurado = totalNoElegible + valorCopago;
        setTotalAsegurado(totalAsegurado.toFixed(2));
    };

    // Llama a la función `recalcularElegiblesNoElegibles` cada vez que se agrega un análisis
    useEffect(() => {
        if (poliza) {
            recalcularElegiblesNoElegibles(analisisAgregados);
        }
    }, [analisisAgregados]);

    const handleElegibleChange = (index) => {
        if (poliza) {
            setAnalisisAgregados((prevAnalisis) => {
                const updatedAnalisis = prevAnalisis.map((analisis, i) => {
                    if (i === index) {
                        return { ...analisis, elegible: !analisis.elegible };
                    }
                    return analisis;
                });

                if (isPolizaValidForOrigen(clienteSeleccionado.idPoliza, polizas)) {
                    recalcularElegiblesNoElegibles(updatedAnalisis);
                }
                return updatedAnalisis;
            });
        }


    };

    // Función para obtener los detalles de la cotización
    const obtenerDetallesCotizacion = async (idCotizacion) => {
        try {
            const response = await fetch(`${server}/CtzCotizacionesDetalles/porIdCotizacion/${idCotizacion}`);
            const data = await response.json();
            if (data && data.$values) {
                // Mapear los datos obtenidos para darles formato adecuado
                const detallesAnalisis = data.$values.map(detalle => ({
                    idDetalle: detalle.idDetalle,
                    idCotizacion: detalle.idCotizacion,
                    idModulo: detalle.idModulo,
                    nombreModulo: detalle.nombreModulo,
                    idPerfil: 0,
                    perfil: detalle.perfil,
                    idTipoAnalisis: detalle.idTipoAnalisis,
                    analisis: detalle.descripcionTipoAnalisis,
                    cantidad: detalle.cantidad,
                    idTipoPrecio: detalle.idTipoPrecio,
                    precio: detalle.valorVenta,
                    descuento: detalle.valorDescuento,
                    total: (parseFloat(detalle.valorVenta) - parseFloat(detalle.valorDescuento)) * parseInt(detalle.cantidad),
                    elegible: detalle.polizaElegible || false,
                    descuentoOriginal: detalle.valorDescuento, // Almacena descuento inicial
                    descuentoDelBackend: true, // 🔴 Flag para indicar que este descuento viene del backend y NO debe recalcularse
                    
                }));
                // Actualizar el estado de analisisAgregados con los detalles obtenidos
                setAnalisisAgregados(detallesAnalisis);
                calcularTotales(detallesAnalisis); // Calcular los totales después de cargar los análisis
            }
        } catch (error) {
            console.error("Error al obtener los detalles de la cotización:", error);
        }
    };


    useEffect(() => {
        if (cotizacion) {
            setFechaCotizacion(cotizacion.fechaCotizacion || '');
            setValidez(cotizacion.duracion || 1);
            setMedidaT(cotizacion.idTipoDuracion || '');
            setTipoPago(cotizacion.idTipoPago || '');
            setMedicoSeleccionado(cotizacion.idMedico || null);
            setOrigen(cotizacion.idOrigen || null);
            setMedioPago(cotizacion.medioPago || null);
            setObservaciones(cotizacion.observaciones || '');
            setAnalisisAgregados(detalles || []);
            setCanalVentaSeleccionado(cotizacion.idCanalDeVentas || null);
            setIsEditing(true); // Indicador de modo edición
            // Busca el cliente en la lista de clientes
            const cliente = clientes.find(c => c.idCliente === cotizacion.idCliente);

            if (cliente) {
                setClienteSeleccionado(cliente);
                setInputValue(cliente.nombreCompleto);
                // Llama a handleClienteSeleccionado para llenar otros campos
                handleClienteSeleccionado(null, cliente);
            } else {
                // Si el cliente no está en la lista, lo obtienes desde la API
                fetch(`${server}/MgClientes/GetByIdCliente/${cotizacion.idCliente}`)
                    .then(response => response.json())
                    .then(data => {
                        console.log(data);
                        setClienteSeleccionado(data);
                        setInputValue(data.nombreCompleto);
                        handleClienteSeleccionado(null, data);
                    })
                    .catch(error => {
                        console.error('Error al obtener el cliente:', error);
                    });
            }
            // console.log(detalles);



            obtenerDetallesCotizacion(cotizacion.idCotizacion);



        } else {
            // Si no hay cotización, inicializamos los estados a sus valores por defecto
            setClienteSeleccionado(null);
            setFechaCotizacion('');
            setValidez(calcularDiasHastaFinDeMes()); // Inicializa con días hasta fin de mes
            
            // Establecer "Días" como valor predeterminado
            const diasOption = medidasTiempo.find(item => item.descripcion.toLowerCase() === 'días' || item.descripcion.toLowerCase() === 'dias');
            if (diasOption) {
                setMedidaT(diasOption.idTipoDuracion);
            }
            
            setTipoPago('');
            setMedicoSeleccionado(null);
            setOrigen(null);
            setMedioPago(null);
            setCanalVentaSeleccionado(null);
            setObservaciones('');
            setWhatsapp(false);
            setApp(false);
            setCorreo(false);
            setImpresos(false);
            setAnalisisAgregados([]);
            setAnalisisSeleccionado(null);
            setIsEditing(false); // Modo creación
        }
    }, [cotizacion]);


      useEffect(() => {
        if (detalles && detalles.length > 0) {
            setAnalisisAgregados(detalles);
            calcularTotales(detalles);
        }
        }, [detalles]);

    useEffect(() => {
        // Establecer fechas iniciales al cargar el componente
        const today = new Date();

        const currentYear = today.getFullYear();
        const currentMonth = String(today.getMonth() + 1).padStart(2, '0'); // Los meses van de 0 (enero) a 11 (diciembre)
        const currentDay = String(today.getDate()).padStart(2, '0');

        const fechaActual = `${currentYear}-${currentMonth}-${currentDay}`;

        setFechaCotizacion(fechaActual);
    }, []);


    const handleSearch = async () => {
        setSearchQuery(inputValue); // Actualiza searchQuery con el valor actual del input
        setPage(0);
        setHasMore(true);
        await fetchClientes(inputValue); // Ejecuta la búsqueda con el texto escrito
    };

    // Manejar la pulsación de la tecla "Enter"
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault(); // Evita que el formulario se recargue
            handleSearch(); // Ejecuta la búsqueda al presionar Enter
        }
    };

    // Fetch de datos desde las APIs para llenar los autocomplete y selects
    useEffect(() => {
        fetchClientes(searchQuery);


        const fetchSexo = async () => {
            try {
                const response = await fetch(`${server}/MgSexoes`);
                const data = await response.json();
                setSexos(data.$values);
            } catch (error) {
                console.error('Error al cargar las sucursales:', error);
            }
        };

        const fetchSucursales = async () => {
            try {
                const response = await fetch(`${server}/MgSucursales`);
                const data = await response.json();
                setSucursales(data.$values);
            } catch (error) {
                console.error('Error al cargar las sucursales:', error);
            }
        };

        const fetchMedicos = async () => {
            try {
                const response = await fetch(`${server}/MgMedicoes`);
                const data = await response.json();
                setMedicos(data.$values);
            } catch (error) {
                console.error('Error al cargar los médicos:', error);
            }
        };

        const fetchOrigen = async () => {
            try {
                const response = await fetch(`${server}/MgOrigenes`);
                const data = await response.json();
                setOrigenes(data.$values);
            } catch (error) {
                console.error('Error al cargar los orígenes:', error);
            }
        };

        const fetchMedioPago = async () => {
            try {
                const response = await fetch(`${server}/MgTarjetasBancarias`);
                const data = await response.json();
                setMediosPago(data.$values);
                
                // Buscar la opción "EFECTIVO" y establecerla por defecto
                const efectivoOption = data.$values.find(item => 
                    item.descripcion && item.descripcion.toUpperCase() === 'EFECTIVO'
                );
                
                if (efectivoOption) {
                    setMedioPago(efectivoOption);
                }
            } catch (error) {
                console.error('Error al cargar los medios de pago:', error);
            }
        };

        const fetchPerfiles = async () => {
            try {
                const idSucursal = sucursalSeleccionada?.idSucursal;
                // const response = await fetch(`${server}/MgAnalisisGeneralPerfiles`);
                const response = await fetch(`${server}/MgAnalisisGeneralPerfiles/vigentesporsucursal/${idSucursal}`);
                const data = await response.json();
                setPerfiles(data.$values);
            } catch (error) {
                console.error('Error al cargar los perfiles:', error);
            }
        };

        const fetchAnalisis = async () => {
            try {
                const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis`);
                const data = await response.json();
                setAnalisis(data.$values);
            } catch (error) {
                console.error('Error al cargar los tipos de analisis:', error);
            }
        };

        const fetchAnalisisPrecios = async () => {
            try {
                const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis/AnalisisPreciosSucursalLista`);
                const data = await response.json();
                setAnalisisPrecios(data.$values);
                console.log(data.$values);
            } catch (error) {
                console.error('Error al cargar los precios de análisis:', error);
            }
        };


        const fetchTiposCotizacion = async () => {
            try {
                const response = await fetch(`${server}/CtzCotizacionesTipoes`);
                const data = await response.json();
                setTiposCotizacion(data.$values);
            } catch (error) {
                console.error('Error al cargar las cotizaciones:', error);
            }
        };

        const fetchTiposPagos = async () => {
            try {
                const response = await fetch(`${server}/CtzTiposDePagoes`);
                const data = await response.json();
                setTiposPagos(data.$values);
            } catch (error) {
                console.error('Error al cargar las cotizaciones:', error);
            }
        };

        const fetchTiposPrecio = async () => {
            try {
                const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisisTiposDePrecios`);
                const data = await response.json();
                setTiposPrecio(data.$values);
            } catch (error) {
                console.error('Error al cargar las cotizaciones:', error);
            }
        };

        const fetchPrioridadesDescuento = async () => {
            try {
                const response = await fetch(`${server}/conf_prioridad_descuento`);
                const data = await response.json();
                setPrioridadDescuento(data.$values);
            } catch (error) {
                console.error('Error al cargar las cotizaciones:', error);
            }
        };

        const fetchMedidasTipos = async () => {
            try {
                const response = await fetch(`${server}/CtzTipoDuracions`);
                const data = await response.json();
                setMedidasTiempo(data.$values);
                
                // Buscar el ID que corresponde a "Días" y establecerlo por defecto
                const diasOption = data.$values.find(item => item.descripcion.toLowerCase() === 'días' || item.descripcion.toLowerCase() === 'dias');
                if (diasOption) {
                    setMedidaT(diasOption.idTipoDuracion);
                }
            } catch (error) {
                console.error('Error al cargar las cotizaciones:', error);
            }
        };

        const fetchPolizas = async () => {
            try {
                const response = await fetch(`${server}/MgPolizasDeSeguroes`);
                const data = await response.json();
                setPolizas(data.$values);
            } catch (error) {
                console.error('Error al cargar las cotizaciones:', error);
            }
        };

    const fetchCanalesDeVenta = async () => {
        try {
            const response = await fetch(`${server}/MgCanalesDeVenta`); 
            const data = await response.json();
            setCanalesVenta(data.$values);
        } catch (error) {
            console.error('Error al cargar los canales de venta:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al cargar los canales de venta: ' + error,
            });
        }
    };

        // Llamar las funciones para obtener los datos
        fetchClientes();
        fetchSucursales();
        fetchMedicos();
        fetchOrigen();
        fetchMedioPago();
        fetchCanalesDeVenta();
        fetchPerfiles();
        fetchAnalisis();
        fetchSexo();
        fetchTiposCotizacion();
        fetchTiposPagos();
        fetchMedidasTipos();
        fetchTiposPrecio();
        fetchAnalisisPrecios();
        fetchPrioridadesDescuento();
        fetchPolizas();
    }, []);

    // Funciones para manejar la paginación de la tabla
    const handleChangeTablePage = (event, newPage) => {
        setTablePage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setTablePage(0);
    };


    const handleOpenModalCliente = () => {
        if (!clienteSeleccionado) {
            Swal.fire({
                title: 'Falta Cliente',
                text: 'Por favor, selecciona un cliente para editar.',
                icon: 'warning',
            });
            return;
        }
        setOpenModalCliente(true);
    };

    const handleCloseModalCliente = () => {
        setOpenModalCliente(false);
    };

    const handleAnalisisKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAgregarAnalisis();
        }
    };

    const handlePerfilKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAgregarAnalisisPerfil();
        }
    };

    const handleCambioDescuento = (idAnalisis, nuevoPorcentaje) => {
  const nuevosAnalisis = analisisAgregados.map(a => {
    if (a.idTipoAnalisis === idAnalisis) {
      return {
        ...a,
        porcentajeDescuento: nuevoPorcentaje,
        descuentoManual: true  // ⚠️ Marcamos que es manual
      };
    }
    return a;
  });
  setAnalisisAgregados(nuevosAnalisis);
  calcularTotales(nuevosAnalisis);
};



    const handleClienteActualizado = async () => {
        // Volver a obtener la información del cliente actualizado
        console.log(clienteSeleccionado.idCliente);
        if (clienteSeleccionado) {
            try {
                const response = await fetch(`${server}/MgClientes/GetByIdCliente/${clienteSeleccionado.idCliente}`);
                const data = await response.json();
                setClienteSeleccionado(data);
                handleClienteSeleccionado(null, data); // Actualizar campos relacionados
            } catch (error) {
                console.error('Error al actualizar la información del cliente:', error);
            }
        }
        // Cerrar el modal
        handleCloseModalCliente();
    };


    const handleEditarCliente = (event) => {

    };

    // Función para calcular totales
    const calcularTotales = (analisisArray) => {
        let valorVentaTotal = 0;
        let descuentoTotalSuma = 0;
        let totalPagarSuma = 0;
        let totalanalsis = 0;

        analisisArray.forEach((analisis) => {
            valorVentaTotal += parseFloat(analisis.precio) * parseInt(analisis.cantidad);
            descuentoTotalSuma += parseFloat(analisis.descuento) * parseInt(analisis.cantidad);
            totalPagarSuma += parseFloat(analisis.total);
            totalanalsis += parseInt(analisis.cantidad);
        });

        const descuentoTotalPorcentaje =
            valorVentaTotal > 0 ? (descuentoTotalSuma / valorVentaTotal) * 100 : 0;

        setValorVenta(valorVentaTotal.toFixed(2));
        setDescuentoTotal(descuentoTotalSuma.toFixed(2));
        setDescuentoTotalP(descuentoTotalPorcentaje.toFixed(2));
        setTotalPagar(totalPagarSuma.toFixed(2));
        setCantidadTotal(totalanalsis);
    };

    const handleAgregarAnalisis = async () => {
   if (isAdding) return; // Previene doble ejecución rápida
        // if (!clienteSeleccionado) {
        //     await Swal.fire({
        //         title: 'Cliente requerido',
        //         text: 'Por favor, selecciona un cliente antes de agregar análisis.',
        //         icon: 'warning',
        //     });
        //     return;
        // }
        if (!analisisSeleccionado) {
            await Swal.fire({
                title: 'Análisis requerido',
                text: 'Por favor, selecciona un análisis.',
                icon: 'warning',
            });
            return;
        }

        if (!analisisListo) {
            Swal.fire({
                icon: "warning",
                text: "El análisis aún está cargando. Intenta nuevamente."
            })
        }

          if (analisisAgregados.some(a => a.idTipoAnalisis === analisisSeleccionado.idTipoAnalisis)) {
            Swal.fire({
                text: "El análisis ya ha sido agregado. No se pueden duplicar.",
                icon: "warning",
                confirmButtonText: "OK",
                allowEnterKey: true,
                allowEscapeKey: true,
                focusConfirm: true,
                buttonsStyling: true
            });
            return;
        }
          setIsAdding(true); // 🔐 Bloquea ejecución múltiple

        const valorNumerico = parseFloat(valor) || 0;
        const cantidadNumerica = parseInt(cantidad) || 1;

        // Usar directamente el valor de descuento definido por el usuario
        // let porcentajeDescuento = parseFloat(descuento) || 0;
        // let porcentajeDescuento = 0;
        let porcentajeDescuento = 0;
        let idPromocionAplicada = null;

        // Si el usuario  borra o pone 0, volver a aplicar descuento automatico
       if (descuentoManualRef.current) {
        // ✅ DESCUENTO MANUAL
        porcentajeDescuento = parseFloat(descuento) || 0;
    } else {
        if (analisisSeleccionado.remitido === 1 && descuentoGlobalRemitido > 0) {
            porcentajeDescuento = descuentoGlobalRemitido;
            idPromocionAplicada = null;
        }

        else if (descuentoGlobal > 0) {
            porcentajeDescuento = descuentoGlobal;
        } else {
            const resultadoDescuento = await obtenerDescuentoPorAnalisis(analisisSeleccionado);
            console.log('resultadoDescuento:', resultadoDescuento);
            porcentajeDescuento = parseFloat(resultadoDescuento?.porcentaje) || 0;
            idPromocionAplicada = resultadoDescuento?.idPromocion || null;
        }
    
    }


        const descuentoMonto = (valorNumerico * porcentajeDescuento) / 100;
        const total = (valorNumerico - descuentoMonto) * cantidadNumerica;

        const nuevoAnalisis = {
            idModulo: analisisSeleccionado.idModulo,
            idPerfil: 0,
            perfil: '',
            idTipoAnalisis: analisisSeleccionado.idTipoAnalisis,
            analisis: analisisSeleccionado.descripcionTipoAnalisis,
            cantidad: cantidadNumerica,
            // idTipoPrecio: analisisSeleccionado.idTipoPrecio,
            idTipoPrecio: tipoPrecio,
            precio: valorNumerico,

            descuentoManual: descuentoManualRef.current, // 🔐 
            porcentajeManual: descuentoManualRef.current ? porcentajeDescuento : null,
            descuento: descuentoMonto.toFixed(2),
            total: total.toFixed(2),

            elegible: elegible,
            comentarios: comentarios,
            idPromocion: idPromocionAplicada,
            remitido: analisisSeleccionado.remitido
        };

        setAnalisisAgregados((prevAnalisis) => {
            const updatedAnalisis = [...prevAnalisis, nuevoAnalisis];
            calcularTotales(updatedAnalisis);
            return updatedAnalisis;
        });

        // Llama al cálculo si es válido según la póliza
        if (clienteSeleccionado && isPolizaValidForOrigen(clienteSeleccionado.idPoliza, polizas)) {
            recalcularElegiblesNoElegibles([...analisisAgregados, nuevoAnalisis]);
        }

        // Limpieza de campos después de agregar
        descuentoManualRef.current = false; // 🔑 reset manual
        setAnalisisSeleccionado(null);
        setInputAnalisisValue('');  // Limpiar también el inputValue del Autocomplete
        setValor(0);
        setCantidad(1);
        setDescuento(0);
        setTipoPrecio('');
        setComentarios('');
        setIsAdding(false); // 🔓 Libera ejecución
        setTimeout(() => recalcularDescuentosCondiciones(), 0);
        setAnalisisListo(false);
    };

        // useEffect(() => {
        //     if (analisisAgregados.length === 0) return;
        //     recalcularDescuentosCondiciones();
        // }, [clienteSeleccionado, origen, medicoSeleccionado, medioPago, tipoPago, isAseguradora, perfilDescuento, analisisAgregados, descuentoGlobal, descuentoGlobalRemitido]);
    



    const handleAgregarAnalisisPerfil = async () => {

        // console.log('Validacion elegibilidad iniciado');
        // console.log('Cliente:' , clienteSeleccionado);
        // console.log('Perfil: ', perfil)

        if (!clienteSeleccionado) {
            Swal.fire({
                text: "Por favor, selecciona un cliente antes de agregar un análisis.",
                icon: "warning"
            });
            return;
        }

        if (!perfil) {
            Swal.fire({
                text: "Por favor, selecciona un perfil.",
                icon: "warning"
    //         });
    //         return;
    //     }

    //     const esClienteElegibleParaPerfil = (cliente, perfil) => {
    //     if (!cliente || !perfil) return false;

    //     const hoy = new Date();

    //     // Validar vigencia
    //     const fechaDesde = new Date(perfil.fechaDesde);
    //     const fechaHasta = new Date(perfil.fechaHasta);
    //     if (hoy < fechaDesde || hoy > fechaHasta) return false;

    //     // Validar sexo (0 = ambos, 1 = masculino, 2 = femenino)
    //     if (perfil.idSexo && perfil.idSexo !== 0 && perfil.idSexo !== cliente.idSexo) return false;

    //     // Validar edad en días
    //     const fechaNacimiento = new Date(cliente.fechaNacimiento);
    //     const diffDias = Math.floor((hoy - fechaNacimiento) / (1000 * 60 * 60 * 24));
    //     if (perfil.edadDesde && diffDias < perfil.edadDesde){
    //         console.log('Cliente es ma sjovem que edadDesde');
    //          return false;
    //     }
    //     if (perfil.edadHasta && diffDias > perfil.edadHasta) {
    //         SmokingRoomsOutlined.log('Cliente es mayor que edadDesde')
    //         return false;
    //     }
    //     return true;
    // };

    // const esElegible = esClienteElegibleParaPerfil(clienteSeleccionado, perfil);

    // if (!esElegible) {
    //     Swal.fire({
    //         title: 'Perfil no aplicable',
    //         icon: 'warning'
        }); 
        return;
    }

        try {
            const response = await fetch(`${server}/MgAnalisisGeneralPerfilesAnalisis/AnalisisbyPerfilbySucursal/${perfil.idPerfil}/${sucursalSeleccionada.idSucursal}`);
            const data = await response.json();

            if (data && data.$values && Array.isArray(data.$values)) {
                // Usar el porcentaje de descuento ingresado por el usuario
                // const porcentajeDescuentoPerfil = parseFloat(descuento) || 0;

                const idsAnalisisPerfil = data.$values.map(a => a.idTipoAnalisis);
                // console.log(data.$values);

                // Validar si algún análisis del perfil ya está agregado
                const idsAnalisisYaAgregados = analisisAgregados.map(a => a.idTipoAnalisis);
                const analisisDuplicados = data.$values.filter(analisisItem => 
                    idsAnalisisYaAgregados.includes(analisisItem.idTipoAnalisis)
                );
                

                // const nuevosAnalisis = data.$values.map((analisisItem) => {
                //     const precio = parseFloat(analisisItem.precio);
                //     const descuentoMonto = (precio * porcentajeDescuentoPerfil) / 100;
                //     const total = (precio - descuentoMonto) * 1; // cantidad = 1


            if (analisisDuplicados.length > 0) {
                const nombresAnalisisDuplicados = analisisDuplicados.map(a => a.descripcionTipoAnalisis).join(', ');
                Swal.fire({
                title: "Análisis Duplicados",
                text: `Los siguientes análisis del perfil ya están agregados: ${nombresAnalisisDuplicados}. No se pueden duplicar análisis.`,
                icon: "warning",
                confirmButtonText: "OK",
                allowEnterKey: true,
                allowEscapeKey: true,
                focusConfirm: true,
                buttonsStyling: true
                });
                return;
            }

            // Obtener el descuento por perfil directamente del objeto
                let porcentajeDescuentoPerfil = 0;
                let descuentoRemitidoPerfil = 0;
                let idPromocionPerfil = null;


                if (descuentoGlobal === 0) {
                    // Usar directamente las propiedades del objeto perfil
                    porcentajeDescuentoPerfil = perfil?.porcentaje || 0;
                    descuentoRemitidoPerfil = perfil?.descuento_remitido || 0;
                    idPromocionPerfil = perfil?.idPromocion || null;
                }

                console.log('Datos de descuento del perfil:', {
                    porcentajeNormal: porcentajeDescuentoPerfil,
                    porcentajeRemitido: descuentoRemitidoPerfil,
                    idPromocion: idPromocionPerfil
                });
        

                const nuevosAnalisis = data.$values.map((analisisItem) => {
                    let porcentajeDescuento = porcentajeDescuentoPerfil;

                    // Si hay descuentoGlobal, este tiene prioridad
                    if (descuentoGlobal > 0) {
                        porcentajeDescuento = descuentoGlobal;
                    }

                    const precio = parseFloat(analisisItem.precio);
                    const descuentoMonto = (precio * porcentajeDescuento) / 100;
                    const total = (precio - descuentoMonto) * 1; // cantidad = 1


                    return {
                        idPerfil: perfil.idPerfil,
                        perfil: perfil.descripcionPerfil,
                        idTipoAnalisis: analisisItem.idTipoAnalisis,
                        analisis: analisisItem.descripcionTipoAnalisis,
                        cantidad: 1,
                        idTipoPrecio: analisisItem.idTipoPrecio,
                        precio: precio,
                        descripcionTipoPrecio: analisisItem.descripcionTipoPrecio,
                        idModulo: analisisItem.idModulo,
                        descuento: descuentoMonto.toFixed(2),
                        total: total.toFixed(2),
                        elegible: false,
                        comentarios: '',
                        idPromocion: idPromocionPerfil,
                        esDePerfil: true
                    };
                });

                // Solo agregar el descuento del perfil si descuentoGlobal es 0 y el porcentaje de descuento del perfil es mayor que 0
                if (descuentoGlobal === 0 && porcentajeDescuentoPerfil > 0) {
                    setDescuentosAplicados((prevDescuentos) => [
                        ...prevDescuentos,
                        {
                            tipo: 'Perfil',
                            porcentaje: porcentajeDescuentoPerfil,
                            idPerfil: perfil.idPerfil,
                            idsAnalisisPerfil: idsAnalisisPerfil,
                            idPromocion: idPromocionPerfil
                        },
                    ]);
                }
                

                // Agregar el descuento del perfil a la lista
                // setDescuentosAplicados((prevDescuentos) => [
                //     ...prevDescuentos,
                //     {
                //         tipo: 'Perfil',
                //         porcentaje: porcentajeDescuentoPerfil,
                //         idPerfil: perfil.idPerfil,
                //         idsAnalisisPerfil: idsAnalisisPerfil,
                //     },
                // ]);
            

                setAnalisisAgregados((prevAnalisis) => {
                    const updatedAnalisis = [...prevAnalisis, ...nuevosAnalisis];
                    // console.log(updatedAnalisis);
                    calcularTotales(updatedAnalisis);
                    return updatedAnalisis;
                });

                if (isPolizaValidForOrigen(clienteSeleccionado.idPoliza, polizas)) {
                    recalcularElegiblesNoElegibles([...analisisAgregados, ...nuevosAnalisis]);
                }

                // Mantener el perfil seleccionado para que los descuentos se apliquen correctamente
                setPerfilDescuento(perfil);
                setPerfil(null);
            } else {
                await Swal.fire({
                    title: 'Sin análisis',
                    text: 'No se encontraron análisis para el perfil seleccionado.',
                    icon: 'info',
                });
            }
        } catch (error) {
            console.error('Error al cargar los análisis del perfil:', error);
            // alert('Hubo un error al cargar los análisis del perfil.');
        }
    };


    const handleEliminarAnalisis = async (index) => {
        // setAnalisisAgregados((prevAnalisis) => {
            // const updatedAnalisis = prevAnalisis.filter((_, i) => i !== index);
            const updatedAnalisis = analisisAgregados.filter((_, i) => i !== index);
            console.log('elimina ' + updatedAnalisis);

            // Actualizar los descuentos
            // setDescuentosAplicados((prevDescuentos) => {
            //     console.log(prevDescuentos);

                // Identificar los perfiles que están incompletos
                // const nuevosDescuentosAplicados = prevDescuentos.filter((descuento) => {
                const nuevosDescuentosAplicados = descuentosAplicados.filter((descuento) => {
                    if (descuento.tipo === 'Perfil') {
                        const idsAnalisisAgregados = updatedAnalisis.map((a) => a.idTipoAnalisis);
                        const perfilCompleto = descuento.idsAnalisisPerfil.every((idAnalisis) =>
                            idsAnalisisAgregados.includes(idAnalisis)
                        );
                        console.log('Perfil Completo:', perfilCompleto);

                        if (!perfilCompleto) {
                            // Si el perfil ya no está completo, eliminar el descuento de ese perfil
                            // y recalcular el descuento para esos análisis individualmente
                            updatedAnalisis.forEach((analisis) => {
                                if (descuento.idsAnalisisPerfil.includes(analisis.idTipoAnalisis)) {
                                    // Aplicar el descuento individual al análisis incompleto
                                    // const descuentoIndividual = await obtenerDescuentoPorAnalisis(analisis.idTipoAnalisis);
                                    // const descuentoMonto = (analisis.precio * descuentoIndividual) / 100;
                                    // analisis.descuento = descuentoMonto.toFixed(2);
                                    // analisis.total = ((analisis.precio - descuentoMonto) * analisis.cantidad).toFixed(2);
                                    analisis.idPerfil = 0;
                                    analisis.perfil = '';
                                }
                            });
                        }

                        return perfilCompleto; // Mantener el descuento si el perfil está completo
                    }
                    return true; // Mantener otros descuentos
                });

                // Asignar el nuevo arreglo de descuentos aplicados
                // return nuevosDescuentosAplicados;
                


                setAnalisisAgregados(updatedAnalisis);
                setDescuentosAplicados(nuevosDescuentosAplicados);
                calcularTotales(updatedAnalisis);

            // Llamar a recalcular descuentos para aplicar las actualizaciones a `analisisAgregados`
            // recalcularDescuentosAnalisisAgregados(updatedAnalisis);

            // Devolver el arreglo actualizado de `analisisAgregados`
            return updatedAnalisis;

            if (modo === 'modificar' && updatedAnalisis.every(a => a.descuentoDelBackend === true)) {
        console.log('🛡️ PROTECCIÓN handleEliminarAnalisis: Modo modificar + Backend data - BLOQUEADO recalcularDescuentosCondiciones');
        return;
    }
    
    setTimeout(() => recalcularDescuentosCondiciones(), 0);
        
    };


    const limpiarFormulario = () => {
        setClienteSeleccionado(null);
        setInputValue('');
        setIdentidad('');
        setEdad('');
        setSexo('');
        setDireccion('');
        setTelefono('');
        setCorreoElectronico('');
        setFechaCotizacion('');
        setValidez(calcularDiasHastaFinDeMes()); // Usar días hasta fin de mes
        
        // Mantener "Días" como valor predeterminado
        const diasOption = medidasTiempo.find(item => item.descripcion.toLowerCase() === 'días' || item.descripcion.toLowerCase() === 'dias');
        if (diasOption) {
            setMedidaT(diasOption.idTipoDuracion);
        }
        setTipoPago('');
        setWhatsapp(false);
        setApp(false);
        setCorreo(false);
        setImpresos(false);
        setMedicoSeleccionado(null);
        setOrigen(null);
        setMedioPago(null);
        setCanalVentaSeleccionado(null);
        setObservaciones('');
        setPerfil('');
        setAnalisisSeleccionado(null);
        setAnalisisAgregados([]);
        setCantidad(1);
        setValor('');
        setDescuento('');
        setTotalPagar('');
        setDescuentoTotal('');
        setDescuentoTotalP('');
        setCantidadTotal('');
        setDescuentoGlobal(0);
        setPoliza('');
        setEmpresa('');
        setNTransaccion('');
        setMinimo('');
        setMaximo('');
        setNVerificacion('');
        setCopago('');
        setAutorizado('');
        setDeducible('');
        setValorElegible('');
        setNoElegibles('');
        setAseguradora('');
        setValorCopago('');
        setTotalAsegurado('');
    };


const handleGuardarCotizacion = async () => {
    try {
        // 1️⃣ Validaciones básicas
        if (!clienteSeleccionado) {
            Swal.fire('Cliente requerido', 'Por favor, selecciona un cliente.', 'warning');
            return;
        }

        if (!validez || !medidaT || !tipoPago || !medioPago) {
            Swal.fire('Campos incompletos', 'Llena los campos de Validez, Tiempo, Tipo de Pago y Medio de Pago.', 'warning');
            return;
        }

        if (!canalVentaSeleccionado) {
            swalWithHighZIndex.fire({
                    title: 'Error',
                    text: 'Debes seleccionar un canal de venta.',
                    icon: 'error',
                    zIndex: 2000
                });
                return;
            }

        if (analisisAgregados.length === 0) {
            Swal.fire('Sin análisis', 'Agrega al menos un análisis a la cotización.', 'warning');
            return;
        }

        setIsGuardando(true);

        // 2️⃣ Detectar si es edición o nueva cotización
        const isEdit = modo === 'modificar' && cotizacion?.idCotizacion > 0;

        // 3️⃣ Datos base de la cotización
        const baseCotizacionData = {
            idCliente: clienteSeleccionado.idCliente,
            correo: clienteSeleccionado.correoElectronico ?? '',
            // fechaCotizacion,
            fechaCotizacion: modo === 'modificar' ? (cotizacion?.fechaCotizacion ?? fechaCotizacion) : fechaCotizacion,
            duracion: validez,
            idTipoDuracion: medidaT,
            idTipoPago: tipoPago,
            valorVenta,
            valorDescuento: descuentoTotal,
            valorRecargo: deducible != null ? Number(deducible) : 0,
            telefono: clienteSeleccionado.telefono?.slice(0, 11) ?? '', // evita error varchar(11)
            celular: clienteSeleccionado.celular,
            idSucursal: sucursalSeleccionada.idSucursal,
            idMedico: medicoSeleccionado?.idMedico ?? 0,
            idOrigen: origen?.idOrigen ?? 0,
            idMedioPago: medioPago?.id ?? null,
            enviarWhatsapp: whatsapp,
            observaciones,
            idTipo: 1,
            idCanalDeVentas: canalVentaSeleccionado || null,
        };

        // 4️⃣ Agregar campos según edición o nueva
        const CotizacionData = isEdit
            ? {
                ...baseCotizacionData,
                idCotizacion: Number(cotizacion.idCotizacion),
                modificadoPor: user?.codigoUsuario ?? 'ADMIN',
                fechaModificacion: new Date().toISOString(),
            }
            : {
                ...baseCotizacionData,
                ingresadoPor: user?.codigoUsuario ?? 'ADMIN',
            };

        // 5️⃣ URL y método
        const url = isEdit
            ? `${server}/Cotizaciones/${cotizacion.idCotizacion}`
            : `${server}/Cotizaciones`;
        const method = isEdit ? 'PUT' : 'POST';

        console.log('🔹 CotizacionData:', JSON.stringify(CotizacionData, null, 2));
        console.log('canalVentaSeleccionado', canalVentaSeleccionado);
        console.log('canalVentaSeleccionado', canalVentaSeleccionado.idCanalDeVentas);

        // 6️⃣ Guardar cotización
        const response = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(CotizacionData),
        });

        if (!response.ok) throw new Error('Error al guardar la cotización');

        let result = {};
        const text = await response.text();
        if (text) result = JSON.parse(text);

        const idCotizacion = result.idCotizacion || cotizacion?.idCotizacion;
        if (!idCotizacion) throw new Error('No se recibió el id de la cotización');
        setIdCotizacion(idCotizacion);

        const username = '' + user.codigoUsuario + '/';

        // 7️⃣ Si es edición, eliminar detalles anteriores
        // if (isEdit) {
        //     // await fetch(`${server}/CtzCotizacionesDetalles/deletebyidcotizacion/${idCotizacion}/${username}`, {
        //     await fetch(`${server}/CtzCotizacionesDetalles/deletebyidcotizacion/${idCotizacion}/${username}`, {
        //         method: 'DELETE',
        //         headers: { 'Content-Type': 'application/json' },
        //     });
        // }

        // // 8️⃣ Agregar los nuevos detalles
        // for (const analisis of analisisAgregados) {
        //     const analisisData = {
        //         idCotizacion: idCotizacion,
        //         idModulo: analisis.idModulo, // Convertir a string si API espera string
        //         idTipoAnalisis: analisis.idTipoAnalisis,
        //         fechaCotizacion,
        //         cantidad: analisis.cantidad,
        //         idTipoPrecio: analisis.idTipoPrecio,
        //         valorVenta: analisis.precio,
        //         valorDescuento: analisis.descuento,
        //         PolizaElegible: analisis.elegible,
        //         ingresadoPor: user?.codigoUsuario ?? 'ADMIN',
        //     };

        //     const analisisResponse = await fetch(`${server}/CtzCotizacionesDetalles`, {
        //         method: 'POST',
        //         headers: { 'Content-Type': 'application/json' },
        //         body: JSON.stringify(analisisData),
        //     });

        //     if (!analisisResponse.ok) throw new Error('Error al guardar el análisis de la cotización');
        // }


        let analisisOriginal = cotizacion?.analisis || [];

         if (isEdit) {
            const respDetalles = await fetch(
                `${server}/CtzCotizacionesDetalles/porIdCotizacion/${idCotizacion}`
            );

            if (respDetalles.ok) {
                const data = await respDetalles.json();
                analisisOriginal = data.$values || data;
            }
        }

        // 🔍 DEBUG
        console.log('=== DEBUG COTIZACION ===');
        console.log('Analisis ORIGINAL (BD):', analisisOriginal);
        console.log('Analisis ACTUAL (UI):', analisisAgregados);

        // Detectar analisis eliminados
        const eliminados = analisisOriginal.filter(orig => !analisisAgregados.some(act => act.idTipoAnalisis === orig.idTipoAnalisis || act.idDetalle === orig.idDetalle));

        for (const eliminado of eliminados) {
            // await fetch(`${server}/CtzCotizacionesDetalles/${eliminado.idDetalle}/${username}`,
            //     {method: 'DELETE'}
            // );
            const idDetalleCotizacion =
                eliminado.idCotizacionDetalle || eliminado.idDetalle;

            if (!idDetalleCotizacion) continue;

            await fetch(
                `${server}/CtzCotizacionesDetalles/${idDetalleCotizacion}/${username}`,
                { method: 'DELETE' }
            );
        }

        // Detectar analisis nuevos
        const nuevos = isEdit
            ? analisisAgregados.filter(act =>
                !analisisOriginal.some(orig => orig.idTipoAnalisis === act.idTipoAnalisis)
            )
            : analisisAgregados;


            for (const analisis of nuevos) {
            const analisisData = {
                idCotizacion: idCotizacion,
                idModulo: analisis.idModulo, // Convertir a string si API espera string
                idTipoAnalisis: analisis.idTipoAnalisis,
                fechaCotizacion,
                cantidad: analisis.cantidad,
                idTipoPrecio: analisis.idTipoPrecio,
                valorVenta: analisis.precio,
                valorDescuento: analisis.descuento,
                PolizaElegible: analisis.elegible,
                ingresadoPor: user?.codigoUsuario ?? 'ADMIN',
            };

            const analisisResponse = await fetch(`${server}/CtzCotizacionesDetalles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(analisisData),
            });

            if (!analisisResponse.ok) throw new Error('Error al guardar el análisis de la cotización');
        }

        // 9️⃣ Mostrar PDF / limpiar formulario
        await handleReimprimirPreview(idCotizacion);

        // if (isEdit) {
        //     onClose(); // cerrar modal
        // } else {
        //     limpiarFormulario(); // limpiar campos
        // }

        //   if (isEdit) {
        //     onClose(); // cerrar modal
        // } else {
        //     limpiarFormulario(); // limpiar campos
        // }

    } catch (error) {
        console.error(error);
        await Swal.fire({
            title: 'Error',
            text: error.message || 'Hubo un error al guardar la cotización.',
            icon: 'error',
        });
    } finally {
        setIsGuardando(false); // desbloquear botón
    }
};



    const handleClienteSeleccionado = (event, newValue) => {
        setClienteSeleccionado(newValue); // Guardar el cliente seleccionado
        // Restablecer el descuento
        console.log(newValue);
        setDescuento(0);
        if (newValue) {
            setIdentidad(newValue.numeroIdentidad); // Asignar valores adicionales
            const fechaNacimiento = new Date(newValue.fechaNacimiento); // Obtener la fecha de nacimiento
            const hoy = new Date(); // Obtener la fecha actual

            // Calcular años y meses
            let edadAnios = hoy.getFullYear() - fechaNacimiento.getFullYear();
            let edadMeses = hoy.getMonth() - fechaNacimiento.getMonth();

            if (edadMeses < 0) {
                edadAnios--; // Restar un año si aún no ha pasado el mes de cumpleaños
                edadMeses += 12; // Ajustar los meses
            }

            const edadCalculada = `${edadAnios} años y ${edadMeses} meses`;

            // Asignar datos de cliente
            setEdad(edadCalculada);
            setSexo(newValue.idSexo);
            setDireccion(newValue.direccionCompleta);
            setTelefono(newValue.telefono);
            setCorreoElectronico(newValue.correoElectronico);

        } else {
            // Si no hay cliente seleccionado, vaciar los campos
            setIdentidad('');
            setEdad('');
            setSexo('');
            setDireccion('');
            setTelefono('');
            setCorreoElectronico('');        }
    };

    // Funciones para manejar el modal de vista previa PDF
    const handleCloseReimprimirPreview = () => {
        if (reimprimirPdfUrl) {
            window.URL.revokeObjectURL(reimprimirPdfUrl);
            setReimprimirPdfUrl(null);
        }
        setOpenReimprimirPreview(false);
        setReimprimirError(null);

        //Limpiar el formulario solo si no estamos en modo modificar
        if (modo !== 'modificar') {
            limpiarFormulario();
        }

        //Solo cerrar el modal principal so el usuario no esta en proceso de facturacion
        if (modo !== 'modificar' && typeof onClose === 'function') {
            onClose();
        }
    };

    const handleReimprimirPreview = async (idCotizacion) => {
        if (!idCotizacion) {
            Swal.fire({ text: 'No se pudo obtener el ID de la cotización.', icon: 'error' });
            return;
        }
        setReimprimirLoading(true);
        setReimprimirError(null);
        setOpenReimprimirPreview(true);
        try {
            const response = await fetch(`${server}/Cotizaciones/ImprimirCotizacion/${idCotizacion}`, {
                method: 'POST',
            });
            if (!response.ok) {
                throw new Error(`Error al generar el PDF: ${response.status} ${response.statusText}`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setReimprimirPdfUrl(url);
        } catch (err) {
            setReimprimirError(err.message || 'Error al generar la vista previa del PDF');
        } finally {
            setReimprimirLoading(false);
        }
    };
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    height: '95vh',
    backgroundColor: '#fff',
    padding: '20px',
    boxShadow: 24,
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    maxHeight: '95vh'
};


    const handleSeleccionAnalisis = async (analisis) => {
            console.log('🔍 [DEBUG] handleSeleccionAnalisis iniciado con:', analisis);
            
            if (!analisis || !analisis.idTipoAnalisis) {
                console.warn('⚠️ [DEBUG] Análisis inválido recibido:', analisis);
    
                // Limpiar si no hay selección
                setTipoPrecio('');
                setValor('');
                setDescuento('');
                setAnalisisSeleccionado(null);
                setTiposPrecioAnalisis([]); // Limpiar tipos de precio disponibles
                console.log('🧹 [DEBUG] Estados limpiados');
                descuentoManualRef.current = false;
                return;
            }
    
            setAnalisisSeleccionado(analisis);
            const { idTipoAnalisis } = analisis;
            const idSucursal = sucursalSeleccionada?.idSucursal;
    
            console.log('📋 [DEBUG] idTipoAnalisis:', idTipoAnalisis, 'idSucursal:', idSucursal);
    
            try {
                // Llamar a la nueva API que devuelve todos los tipos de precio para este análisis y sucursal
                const url = `${server}/MgAnalisisGeneralPerfilesAnalisis/AnalisispreciobySucursal/${idTipoAnalisis}/${idSucursal}`;
                console.log('🌐 [DEBUG] Llamando API:', url);
                
                const response = await fetch(url);
    
                if (!response.ok) {
                    console.error('❌ [DEBUG] Error en response:', response.status, response.statusText);
                    throw new Error('Error en la API');
                }
    
                const apiResult = await response.json();
                console.log('📥 [DEBUG] Respuesta completa de la API:', apiResult);
                
                const preciosData = apiResult.$values || apiResult;
                console.log('💰 [DEBUG] Datos de precios extraídos:', preciosData);
    
                if (!preciosData || preciosData.length === 0) {
                    console.log('⚠️ [DEBUG] No hay datos de precios');
                    await Swal.fire({
                        icon: 'warning',
                        title: 'Precio no encontrado',
                        text: 'No se encontró precio para el análisis seleccionado en la sucursal actual.',
                    });
                    setTipoPrecio('');
                    setValor('');
                    setDescuento('');
                    setTiposPrecioAnalisis([]);
                    return;
                }
    
                // Actualizar los tipos de precio disponibles para este análisis
                const tiposPrecioDisponibles = preciosData.map(precio => ({
                    idTipoPrecio: precio.idTipoPrecio,
                    descripcionTipoPrecio: precio.descripcionTipoPrecio,
                    precio: precio.precio
                }));
                
                console.log('🏷️ [DEBUG] Tipos de precio disponibles:', tiposPrecioDisponibles);
                setTiposPrecioAnalisis(tiposPrecioDisponibles);
    
                // Seleccionar el primer tipo de precio disponible
                const primerTipoPrecio = preciosData[0];
                console.log('🥇 [DEBUG] Primer tipo de precio:', primerTipoPrecio);
                
                setTipoPrecio(primerTipoPrecio.idTipoPrecio);
                setValor(primerTipoPrecio.precio);
                
                console.log('✅ [DEBUG] Estados actualizados - tipoPrecio:', primerTipoPrecio.idTipoPrecio, 'valor:', primerTipoPrecio.precio);
    
                // No recalcular descuento si es manual
                if (descuentoManualRef.current) {
                    console.log("Descuento manual activo, no se recalcula");
                    return;
                }

                // Calcular descuento
                const descuentoGlobalAplicable = analisis.remitido ? descuentoGlobalRemitido : descuentoGlobal;
    
                if (descuentoGlobalAplicable === 0) {
                    try {
                        const resultadoDescuento = await obtenerDescuentoPorAnalisis(idTipoAnalisis);
                        const porcentajeDescuento = resultadoDescuento?.porcentaje || 0;
                        setDescuento(porcentajeDescuento);
                    } catch (error) {
                        console.error('Error al obtener el descuento:', error);
                        setDescuento(0);
                    }
                } else {
                    setDescuento(descuentoGlobalAplicable);
                }
    
            } catch (error) {
                console.error('❌ [DEBUG] Error obteniendo precios por API:', error);
                setTipoPrecio('');
                setValor('');
                setDescuento('');
                setTiposPrecioAnalisis([]);
                await Swal.fire({
                    icon: 'warning',
                    title: 'Error',
                    text: 'Error al obtener los precios del análisis.',
                });
            }
        };


    const handleAgregarAnalisisConParametro = async (analisis) => {
        if (!analisis) {
            Swal.fire({ text: "Por favor, selecciona un análisis.", icon: "warning" });
            return;
        }

        if (!clienteSeleccionado) {
            Swal.fire({ text: "Por favor, selecciona un cliente antes de agregar un análisis.", icon: "warning" });
            return;
        }

        const yaAgregado = analisisAgregados.some(a => a.idTipoAnalisis === analisis.idTipoAnalisis);
        const enProceso = analisisEnProcesoRef.current.has(analisis.idTipoAnalisis);

        if (yaAgregado || enProceso) {
            Swal.fire({ text: "El análisis ya está en proceso o agregado.", icon: "warning" });
            return;
        }

        analisisEnProcesoRef.current.add(analisis.idTipoAnalisis);

        try {
            const idTipoAnalisis = analisis.idTipoAnalisis;
            const idSucursal = sucursalSeleccionada?.idSucursal;

            let precioData = analisisPrecios.find(
                (item) => item.idTipoAnalisis === idTipoAnalisis && item.idSucursal === idSucursal
            );

            if (!precioData) {
                const res = await fetch(
                    `${server}/MgAnalisisGeneralPerfilesAnalisis/AnalisispreciobySucursal/${idTipoAnalisis}/${idSucursal}`
                );
                if (!res.ok) throw new Error('Error en la API');
                precioData = await res.json();
                if (!precioData || !precioData.precio) throw new Error('Sin datos de precio');
            }

            const valorNumerico = parseFloat(precioData.precio) || 0;
            const tipoPrecioId = precioData.idTipoPrecio;
            const cantidadNumerica = parseInt(cantidad) || 1;

            // Usar el descuento definido por el usuario
            let porcentajeDescuento = parseFloat(descuento) || 0;
            let idPromocionAplicada = null;
            
            // No buscamos descuentos automáticos, usamos el valor ingresado por el usuario

            const descuentoMonto = (valorNumerico * porcentajeDescuento) / 100;
            const total = (valorNumerico - descuentoMonto) * cantidadNumerica;

            const nuevoAnalisis = {
                idModulo: analisis.idModulo,
                idPerfil: 0,
                perfil: '',
                idTipoAnalisis: analisis.idTipoAnalisis,
                analisis: analisis.descripcionTipoAnalisis,
                cantidad: cantidadNumerica,
                idTipoPrecio: tipoPrecioId,
                precio: valorNumerico,
                descuento: descuentoMonto.toFixed(2),
                total: total.toFixed(2),
                elegible: elegible,
                comentarios: comentarios,
                idPromocion: idPromocionAplicada,
                remitido: analisis.remitido
            };

            setAnalisisAgregados((prev) => {
                const updated = [...prev, nuevoAnalisis];
                calcularTotales(updated);
                return updated;
            });

            if (isPolizaValidForOrigen(clienteSeleccionado.idPoliza, polizas)) {
                recalcularElegiblesNoElegibles([...analisisAgregados, nuevoAnalisis]);
            }

            // Limpiar
            setAnalisisSeleccionado(null);
            setValor(0);
            setCantidad(1);
            setDescuento(0);
            setComentarios('');

        } catch (err) {
            console.error("Error al agregar análisis:", err);
        } finally {
            analisisEnProcesoRef.current.delete(analisis.idTipoAnalisis);
        }
    };




    // Función para manejar el envío por WhatsApp
    const handleEnviarWhatsapp = async () => {
        // Cerramos el modal de vista previa para evitar problemas de foco
        if (openReimprimirPreview) {
            handleCloseReimprimirPreview();
        }
        
        // Verificamos que la cotización tenga ID
        if (!idCotizacion) {
            console.error("Error: No se puede enviar por WhatsApp, falta ID de cotización");
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo identificar la cotización para enviar por WhatsApp',
            });
            return;
        }
        
        console.log("Enviando cotización por WhatsApp, ID:", idCotizacion);
        
        const { value: numeroWhatsapp } = await Swal.fire({
            title: 'Ingrese el número de WhatsApp',
            input: 'text',
            inputLabel: 'Número de WhatsApp',
            inputPlaceholder: '+50433333333',
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            focusConfirm: false,
            focusInput: true,
            inputAutoFocus: true,
            inputAutoTrim: true,
            inputValidator: (value) => {
                if (!value) {
                    return 'Por favor ingrese un número de WhatsApp';
                }
                // Validar que el número de WhatsApp tenga un formato adecuado
                const whatsappRegex = /^\+?[0-9]{1,4}[0-9]{10,15}$/;
                if (!whatsappRegex.test(value)) {
                    return 'Por favor ingrese un número de WhatsApp válido, incluyendo el código de área si es necesario';
                }
            }
        });
        
        if (numeroWhatsapp) {
            try {
                // Utilizamos el endpoint exacto que usa el componente Cotizaciones
                const response = await fetch(`${server}/Whatsapp/PostCotizaciontoWhatsapp?idCotizacion=${idCotizacion}&numeroWhatsapp=${numeroWhatsapp}`, {
                    method: 'POST',
                });

                if (response.ok) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Whatsapp Enviado',
                        showConfirmButton: false,
                        timer: 1500
                    });
                } else {
                    // Manejo detallado del error igual que en Cotizaciones.jsx
                    const errorData = await response.json();
                    const errorMessage = errorData.message || (errorData.errors ? Object.values(errorData.errors).flat().join(', ') : 'Ocurrió un error inesperado');
                    
                    Swal.fire({
                        icon: 'error',
                        title: `Error ${response.status}`,
                        text: errorMessage,
                    });
                }
            } catch (error) {
                console.error('Error al enviar por WhatsApp:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error Enviar Whatsapp',
                    text: error.message || 'Ocurrió un error inesperado',
                });
            }
        }
    };

    // Función para crear orden de trabajo a partir de la cotización
    const handleCrearOrdenTrabajo = async () => {
        
        // Cerramos el modal de vista previa para evitar problemas de foco
        if (openReimprimirPreview) {
            handleCloseReimprimirPreview();
        }

        // Verificamos que tengamos una cotización válida con ID
        if (!idCotizacion) {
            console.error("Error: No hay una cotización válida con ID");
            Swal.fire({
                title: 'Error',
                text: 'No se pudo identificar la cotización. Por favor, intente de nuevo.',
                icon: 'error'
            });
            return;
        }

        // Mostrar confirmación al usuario igual que en el componente Cotizaciones
        const result = await Swal.fire({
            title: '¿Está seguro?',
            text: '¿Desea crear una Orden de Trabajo a partir de la cotización seleccionada?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, crear',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: false
        });

        if (result.isConfirmed) {
            try {
                // Paso 1: Obtener la cotización completa
                const responseCotizacion = await fetch(`${server}/Cotizaciones/${idCotizacion}`);
                if (!responseCotizacion.ok) {
                    throw new Error('No se pudo obtener la información de la cotización');
                }
                const cotizacionCompleta = await responseCotizacion.json();

                // Paso 2: Obtener los detalles de la cotización
                const responseDetalles = await fetch(`${server}/CtzCotizacionesDetalles/porIdCotizacion/${idCotizacion}`);
                if (!responseDetalles.ok) {
                    throw new Error('No se pudieron obtener los detalles de la cotización');
                }
                const detallesCotizacion = await responseDetalles.json();

                //Validar examenes repetidos
                const detalles = detallesCotizacion.$values;

                const examenesRepetidos = detalles.reduce((acc, item) => {
                    const id = item.idTipoAnalisis;

                    if (!acc[id]) {
                        acc[id] = {
                            cantidad: 0,
                            descripcion: item.descripcionTipoAnalisis
                        };
                    }

                    acc[id].cantidad += item.cantidad || 1;
                    
                    return acc;
                }, {});

                const repetidos = Object.values(examenesRepetidos)
                    .filter(x => x.cantidad > 1);

                if (repetidos.length > 0 ){
                     Swal.fire({
                        icon: 'warning',
                        title: 'No se puede crear la Orden de Trabajo',
                        html: `
                            La cotización contiene exámenes repetidos:<br/><br/>
                            <b>${repetidos.map(x => `${x.descripcion} (${x.cantidad})`).join('<br/>')}</b>
                        `,
                        confirmButtonColor: "primary"
                    });

                    return;
                }

                // Obtener el correlativo
                const responseCant = await fetch(`${server}/MgOrdenesMedicas/cantidad`);
                const data = await responseCant.json();
                const correlativoActual = parseInt(data.toString().slice(4)); // Eliminar los primeros 4 dígitos
                const nuevoCorrelativo = correlativoActual + 1;

                // Obtener el año y el mes actuales
                const fechaActual = new Date();
                const year = fechaActual.getFullYear().toString().slice(-2); // Últimos 2 dígitos del año
                const month = (fechaActual.getMonth() + 1).toString().padStart(2, '0'); // Mes con dos dígitos

                // Construir idOrdenMedicaMes
                const idOrdenMedicaMes = `${year}${month}${nuevoCorrelativo}`;

                // Para fechaCotizacion (solo fecha)
                const currentYear = fechaActual.getFullYear();
                const currentMonth = String(fechaActual.getMonth() + 1).padStart(2, '0');
                const currentDay = String(fechaActual.getDate()).padStart(2, '0');
                const fechaActual1 = `${currentYear}-${currentMonth}-${currentDay}`; // Formato YYYY-MM-DD

                // Para fechaRecepcion y fechaEntrega (fecha y hora)
                const currentHours = String(fechaActual.getHours()).padStart(2, '0');
                const currentMinutes = String(fechaActual.getMinutes()).padStart(2, '0');
                const fechaHoraActual = `${fechaActual1}T${currentHours}:${currentMinutes}`; // Formato YYYY-MM-DDTHH:MM

                // Paso 3: Crear la Orden de Trabajo (encabezado)
                const nuevaOrdenPreparada = {
                    // idOrdenMedicaMes: idOrdenMedicaMes,
                    idOrdenMedicaMes: "",
                    idCliente: cotizacionCompleta.idCliente,
                    correo: cotizacionCompleta.correo || '',
                    idBarrioColonia: 0,
                    fechaOrdenMedica: fechaActual1,
                    duracion: cotizacionCompleta.duracion,
                    idTipoDuracion: cotizacionCompleta.idTipoDuracion,
                    idTipoPago: cotizacionCompleta.idTipoPago,
                    valorVenta: cotizacionCompleta.valorVenta,
                    valorDescuento: cotizacionCompleta.valorDescuento,
                    valorRecargo: cotizacionCompleta.valorRecargo || 0,
                    telefono: cotizacionCompleta.telefono || "",
                    celular: cotizacionCompleta.celular || '',
                    idSucursal: cotizacionCompleta.idSucursal,
                    idMedico: cotizacionCompleta.idMedico || 0,
                    idOrigen: cotizacionCompleta.idOrigen || 0,
                    idMedioPago: 0,
                    idCanalDeVentas: cotizacionCompleta.idCanalDeVentas,
                    enviarXWhatsapp: cotizacionCompleta.enviarWhatsapp || false,
                    enviarXCorreo: false,
                    enviarXApp: false,
                    imprimir: false,
                    porcetajeCopago: 0,
                    idPoliza: 0,
                    polizaValorMinimo: 0,
                    polizaValorMaximo: 0,
                    polizaPrevalidado: false,
                    idSucursalEntrega: cotizacionCompleta.idSucursal,
                    polizaNumeroTransaccion: '',
                    polizaNumeroVericacion: '',
                    polizaDeduccible: 0,
                    polizaElegible: 0,
                    idTipo: 1,
                    idTipoCliente: 1,
                    ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
                    fechaRecepcion: fechaHoraActual,
                    fechaEntrega: fechaHoraActual,
                    idOrdenParaControl: 0,
                    observaciones: ''
                };


                const detallesParaTabla = detallesCotizacion.$values.map(detalle => {
                const precio = parseFloat(detalle.valorVenta) || 0;
                const descuento = parseFloat(detalle.valorDescuento) || 0;
                const total = (precio - descuento).toFixed(2);
                const porcentajeManual = precio > 0
                    ? (descuento / precio) * 100
                    : 0;

                return {
                    idModulo: detalle.idModulo,
                    idTipoAnalisis: detalle.idTipoAnalisis,
                    // analisis: String(detalle.idTipoAnalisis),
                    analisis: detalle.descripcionTipoAnalisis || String(detalle.idTipoAnalisis),
                    cantidad: detalle.cantidad,
                    idTipoPrecio: detalle.idTipoPrecio || 0,
                    precio: precio,
                    //Descuento conservado
                    descuento: descuento.toFixed(2),
                    total: total,

                    descuentoManual: true,
                    porcentajeManual: porcentajeManual,
                    descuentoDelBackend: true, // ⭐ muy importante
                    descuentoOriginal: detalle.valorDescuento, 
                    elegible: detalle.polizaElegible || false,
                    comentarios: '',
                    idPromocion: detalle.idPromocion || 0,
                    remitido: detalle.remitido || false
                };
                });

                

                 setOrdenTrabajoData({
                    ordenTrabajo: {
                    ...nuevaOrdenPreparada,
                    idCotizacion: idCotizacion,
                 
                    },
                    detalles: detallesParaTabla
                });


                // 🟢 Coloca el console.log aquí:
console.log("detalles listos para modal:", detallesParaTabla);
console.log("ordenTrabajo a pasar al modal:", nuevaOrdenPreparada);

                setOpenOrdenTrabajoModal(true);
                
                // Ya no cerramos el modal actual hasta que el usuario interactúe con la orden de trabajo
            } catch (error) {
                console.error('Error al crear la Orden de Trabajo:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.message || 'Ocurrió un error al crear la Orden de Trabajo.',
                });
            }
        }
    };

    const prioridadDescuentoRef = typeof prioridadDescuento !== 'undefined' ? prioridadDescuento : [];

    return (
        <>
            <GestorDescuentos
                origen={origen}
                medicoSeleccionado={medicoSeleccionado}
                clienteSeleccionado={clienteSeleccionado}
                medioPago={medioPago}
                fechaCotizacion={fechaCotizacion}
                analisisAgregados={analisisAgregados || []}
                setAnalisisAgregados={setAnalisisAgregados}
                setDescuentoGlobal={setDescuentoGlobal}
                setDescuentoGlobalRemitido={setDescuentoGlobalRemitido}
                setDescuentoAsegurado={setDescuentoAsegurado}
                calcularTotales={calcularTotales}
                valorCopago={valorCopago || 0}
                server={server}
                perfilSeleccionado={perfilDescuento} // Pasar el perfil seleccionado
                prioridadDescuento={prioridadDescuentoRef}
            />

            <div className="right_col" role="main" style={{ 
            height: '100vh', 
            overflowY: 'auto',
            paddingBottom: '20px'
        }}>
            <div className="x_content" style={{ 
                maxHeight: '100%',
                overflowY: 'auto'
            }}>
                    <Grid container spacing={2}>
                        {/* Sección de Datos del Cliente */}
                        <Grid item xs={12}>
                            <h2>Datos del Cliente</h2>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Autocomplete
                                size="small"
                                freeSolo
                                fullWidth
                                options={clientes}
                                value={clienteSeleccionado}
                                getOptionLabel={(option) => option?.nombreCompleto || ''}
                                filterOptions={createSmartFilter(['nombreCompleto', 'identidad', 'telefono'])}
                                inputValue={inputValue}
                                isOptionEqualToValue={(option, value) => option?.idCliente === value?.idCliente}
                                onInputChange={handleInputChange}
                                onChange={handleClienteSeleccionado}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Buscar cliente"
                                        variant="outlined"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                
                                                // Obtener el texto actual del input
                                                const searchText = e.target.value;
                                                
                                                // Función para manejar la selección después de la llamada a la API
                                                const handleEnterSelect = async () => {
                                                    try {
                                                        // Mostrar indicador de carga
                                                        setIsLoading(true);
                                                        
                                                        // Llamar directamente a la API y esperar su respuesta
                                                        const response = await fetch(`${server}/MgClientes/buscador/${searchText}`, {
                                                            method: 'GET',
                                                            headers: { 'Content-Type': 'application/json' },
                                                        });
                                                        
                                                        const data = await response.json();
                                                        
                                                        // Actualizar la lista de clientes con los resultados de la API
                                                        if (data && data.clientes && Array.isArray(data.clientes.$values)) {
                                                            const clientesData = data.clientes.$values;
                                                            setClientes(clientesData);
                                                            
                                                        }
                                                    } catch (error) {
                                                        console.error('Error al cargar los clientes:', error);
                                                    } finally {
                                                        setIsLoading(false);
                                                    }
                                                };
                                                
                                                // Ejecutar la función asíncrona
                                                handleEnterSelect();
                                            }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />     
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField size="small" label="Identidad" value={identidad || ''} fullWidth inputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField size="small" label="Edad" value={edad || ''} fullWidth inputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth required size="small">
                                <InputLabel>Sexo</InputLabel>
                                <Select
                                    size="small"
                                    value={sexo || ''} // Asegúrate de que 'sexo' tenga un valor válido
                                    onChange={(e) => setSexo(e.target.value)}
                                    required
                                    inputProps={{ readOnly: true }}
                                >
                                    {sexos.map((sexo) => (
                                        <MenuItem key={sexo.idSexo} value={sexo.idSexo}>
                                            {sexo.descripcionSexo}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={1}>
                            <Button sx={{padding:'7px 6px', minWidth:'auto'}} size="small" variant="contained" color="primary" onClick={handleOpenModalCliente} disabled={!tienePermiso('Editar Cliente desde Cotización')}>Editar Cliente</Button>
                        </Grid>
                        <Grid item xs={7}>
                            <TextField size="small" label="Dirección" value={direccion || ''} fullWidth inputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={2}>
                            <TextField size="small" label="Celular" value={telefono || ''} fullWidth inputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField size="small" label="Correo Electronico" value={correoElectronico || ''} fullWidth inputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField size="small" label="Fecha" value={fechaCotizacion || ''} fullWidth type="date" InputLabelProps={{ shrink: true, readOnly: true }} onChange={(e) => setFechaCotizacion(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField 
                                size="small" 
                                label="Validez (días)" 
                                type="number" 
                                value={validez || ''} 
                                fullWidth 
                                InputLabelProps={{ shrink: true }} 
                                onChange={(e) => setValidez(e.target.value)} 
                                inputProps={{
                                    min: 0,
                                    max: 100,
                                }} 
                                helperText="Días hasta fin de mes" 
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton 
                                                size="small" 
                                                onClick={() => setValidez(calcularDiasHastaFinDeMes())}
                                                title="Recalcular días hasta fin de mes"
                                            >
                                                <RefreshIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth required>
                                <InputLabel>Tiempo</InputLabel>
                                <Select
                                    size="small"
                                    value={medidaT || ''} // Asegúrate de que 'sexo' tenga un valor válido
                                    onChange={(e) => setMedidaT(e.target.value)}
                                    required

                                >
                                    {medidasTiempo.map((tipoP) => (
                                        <MenuItem key={tipoP.idTipoDuracion} value={tipoP.idTipoDuracion}>
                                            {tipoP.descripcion}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth required size="small">
                                <InputLabel>Tipo de Pago</InputLabel>
                                <Select
                                    size="small"
                                    value={tipoPago || ''} // Asegúrate de que 'sexo' tenga un valor válido
                                    onChange={(e) => setTipoPago(e.target.value)}
                                    required

                                >
                                    {tiposPagos.map((tipoP) => (
                                        <MenuItem key={tipoP.idTipoPago} value={tipoP.idTipoPago}>
                                            {tipoP.descripcion}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={1}>
                            <FormControlLabel
                                size="small"
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={whatsapp}
                                        onChange={(e) => setWhatsapp(e.target.checked)}
                                    />
                                }
                                label="Enviar por WhatsApp"
                            />
                        </Grid>


                        {/* Sección de Datos de la Cotización */}
                        <Grid item xs={12} style={{padding:'0px'}}>
                            <h2>Datos de la Cotización</h2>
                        </Grid>
                        <Grid item xs={12} md={4}>

                            <Autocomplete
                                size="small"
                                options={Medicos}
                                value={medicoSeleccionado}
                                onChange={(e, newValue) => {
                                    setMedicoSeleccionado(newValue);
                                    console.log("Médico seleccionado:", newValue);
                                }}
                                getOptionLabel={(option) => option.nombreMedico || ''}
                                isOptionEqualToValue={(option, value) => option?.idMedico === value?.idMedico}

                                filterOptions={(options, params) => {
                                    const input = normalizeText(params.inputValue || '');

                                    // 🔹 Caso especial: mostrar SOLO N/A si el usuario escribe "n/a"
                                    if (input === 'n/a') {
                                        return options.filter(
                                            opt => normalizeText(opt.nombreMedico) === 'n/a'
                                        );
                                    }

                                    // 🔹 Aplicar createSmartFilter normalmente
                                    const filtered = createSmartFilter(['nombreMedico'], 25)(options, params);

                                    // 🔹 Excluir N/A del resto de los resultados
                                    return filtered.filter(
                                        opt => normalizeText(opt.nombreMedico) !== 'n/a'
                                    );
                                }}

                                renderInput={(params) =>
                                    <TextField
                                        {...params}
                                        label="Nombre del Médico"
                                        variant="outlined"
                                    />
                                }
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                size="small"
                                options={origenes}
                                value={origen}
                                onChange={(e, newValue) => setOrigen(newValue)}
                                onInputChange={(e, inputValue, reason) => {
                                // 🔹 Limpiar la selección anterior si el usuario escribe algo
                                if (reason === "input"  && inputValue.length > lastInput.length) {
                                    setOrigen(null);
                                }

                                setLastInput(inputValue);
                                }}
                                getOptionLabel={(option) => option?.descripcion || ''}
                                isOptionEqualToValue={(option, value) => option?.idOrigen === value?.idOrigen}
                                filterOptions={(options, params) => {
                                const inputOrigenes = normalizeText(params.inputValue ||"");

                                // 🔹 Caso especial: N/A solo si el input es exactamente "n/a"
                                if (inputOrigenes === 'n/a') {
                                    return options.filter(opt => normalizeText(opt.descripcion) === 'n/a');
                                }

                                // 🔹 Usar createSmartFilter normalmente, pero excluir N/A
                                const filtered = createSmartFilter(['descripcion'], 25)(options, params);
                                    return filtered.filter(opt => normalizeText(opt.descripcion) !== 'n/a');
                                }}
                                renderInput={(params) => <TextField {...params} label="Origen" variant="outlined" />}
                                    // renderOption={(props, option) => <li {...props}>{option.descripcion || ''}</li>}
                                />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Autocomplete
                                size="small"
                                options={mediosPago}
                                value={medioPago}
                                onChange={(e, newValue) => {
                                    setMedioPago(newValue);

                                }}
                                getOptionLabel={(option) => option.descripcion || ''}
                                filterOptions={createSmartFilter(['descripcion'])}
                                renderInput={(params) => <TextField {...params} label="Medio de Pago" variant="outlined" />}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                             <FormControl fullWidth required size="small">
                                <InputLabel>Canal de Venta</InputLabel>
                                <Select
                                    size="small"
                                    value={canalVentaSeleccionado} 
                                    onChange={(e) => setCanalVentaSeleccionado(e.target.value)}
                                    required

                                >
                                    {canalesVenta.map((canalV) => (
                                        <MenuItem key={canalV.id} value={canalV.id}>
                                            {canalV.descripcion}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                            {isAseguradora && (
                                <Typography variant="h5" style={{ color: 'red' }}>
                                    Aseguradora
                                </Typography>
                            )}
                        </Grid>


                        {/* Sección de Datos del Análisis */}
                        <Grid item xs={12} style={{padding:'0px'}}>
                            <h2>Datos de Análisis</h2>
                        </Grid>
                        {/* <Grid container>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    size="small"
                                    fullWidth
                                    options={perfiles}
                                    getOptionLabel={(option) => option.descripcionPerfil || ''}
                                    filterOptions={createSmartFilter(['descripcionPerfil'])}
                                    value={perfil}
                                    onChange={async (event, newValue) => {
                                        setPerfil(newValue);
                                        if (descuentoGlobal === 0) {
                                            if (newValue) {
                                                const porcentajeDescuentoPerfil = await obtenerDescuentoPorPerfil(newValue.idPerfil);
                                                setDescuento(porcentajeDescuentoPerfil);
                                            } else {
                                                setDescuento(0);
                                            }
                                        }
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Selecciona un Perfil" variant="outlined" onKeyDown={handlePerfilKeyDown} />}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Button size="small" variant="contained" color="primary" onClick={handleAgregarAnalisisPerfil}>Agregar Analisis de Perfil</Button>
                            </Grid>
                        </Grid> */}
                        <Grid container>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    size="small"
                                    fullWidth
                                    options={perfiles}
                                    getOptionLabel={(option) => option && option.descripcionPerfil ? option.descripcionPerfil : ''}
                                    filterOptions={createSmartFilter(['descripcionPerfil'])}
                                    value={perfil}
                                    onChange={async (event, newValue) => {

                                         if (newValue) {
                                            console.log('✅ Perfil seleccionado en Autocomplete:', newValue);
                                            console.log('   - ID:', newValue.idPerfil);
                                            console.log('   - Descripción:', newValue.descripcionPerfil);
                                            console.log('   - Porcentaje de descuento:', newValue.porcentaje);
                                            console.log('   - Descuento remitido:', newValue.descuento_remitido);

                                            if (newValue.porcentaje === undefined || newValue.porcentaje === null) {
                                                console.warn('⚠️ El perfil no tiene porcentaje de descuento definido');
                                            }
                                                setPerfilDescuento(newValue);
                                         } else {
                                            console.log('❌ Se ha deseleccionado el perfil');
                                         }

                                        setPerfil(newValue);
                                        // if (descuentoGlobal === 0) {
                                        //     if (newValue) {
                                        //         const porcentajeDescuentoPerfil = await obtenerDescuentoPorPerfil(newValue.idPerfil);
                                        //         setDescuento(porcentajeDescuentoPerfil);
                                        //     } else {
                                        //         setDescuento(0);
                                        //     }
                                        // }
                                    }}
                                    isOptionEqualToValue={(option, value) => option?.idPerfil === value?.idPerfil} // Comparar por ID de perfil
                                    renderOption={(props, option) => (
                                        <li 
                                            {...props} 
                                            style={{
                                                ...props.style,
                                                backgroundColor: option.porcentaje>0 ? '#e8f5e8' : 'transparent',
                                                color: option.porcentaje>0 ? '#2e7d32' : 'inherit',
                                                fontWeight: option.porcentaje>0 ? 'bold' : 'normal',
                                                borderLeft: option.porcentaje>0 ? '4px solid #4caf50' : 'none',
                                                paddingLeft: option.porcentaje>0 ? '12px' : '16px'
                                            }}
                                        >
                                            {option.descripcionPerfil || ''}
                                            {option.porcentaje>0 && (
                                                <span style={{ 
                                                    marginLeft: '8px', 
                                                    fontSize: '0.8em', 
                                                    backgroundColor: '#4caf50', 
                                                    color: 'white', 
                                                    padding: '2px 6px', 
                                                    borderRadius: '10px' 
                                                }}>
                                                    Vigente
                                                </span>
                                            )}
                                        </li>
                                    )}
                                    renderInput={(params) => <TextField {...params} label="Selecciona un Perfil" variant="outlined" onKeyDown={handlePerfilKeyDown} />}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Button size="small" variant="contained" color="primary" onClick={handleAgregarAnalisisPerfil} disabled={!tienePermiso('Agregar Perfil Análisis Cotización')}>Agregar Analisis de Perfil</Button>
                            </Grid>
                        </Grid>
                        {/* <Grid item xs={12} md={4}>
                            <Autocomplete
                                size="small"
                                // autoHighlight
                                autoSelect={false}
                                options={analisis}
                                value={analisisSeleccionado}
                                getOptionLabel={(option) => option?.descripcionTipoAnalisis || ''}
                                filterOptions={createRestrictiveFilter(
                                    ['descripcionTipoAnalisis'],
                                    20,
                                    analisisAgregados
                                )}
                                isOptionEqualToValue={(option, value) =>
                                    option?.idTipoAnalisis === value?.idTipoAnalisis
                                }
                                onChange={async (_, newValue) => {
                                    // Si se limpia el autocomplete
                                    if (!newValue) {
                                        setAnalisisSeleccionado(null);
                                        return;
                                    }

                                    // Evitar duplicados
                                    if (
                                        analisisAgregados.some(
                                            a => a.idTipoAnalisis === newValue.idTipoAnalisis
                                        )
                                    ) {
                                        Swal.fire({
                                            icon: 'warning',
                                            title: 'Análisis ya agregado',
                                            text: 'El análisis ya ha sido agregado. No se pueden duplicar.',
                                            timer: 1500,
                                            showConfirmButton: false,
                                            toast: true,
                                            position: 'top-end'
                                        });
                                        setAnalisisSeleccionado(null);
                                        return;
                                    }

                                    // Selección normal
                                    await handleSeleccionAnalisis(newValue);
                                    setAnalisisSeleccionado(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        label="Análisis"
                                        variant="outlined"
                                        inputRef={refAutocompleteAnalisis}
                                        onKeyDown={async (e) => {
                                            if (e.key !== 'Enter') return;

                                            e.preventDefault();

                                            if (enterLockRef.current) return;
                                            enterLockRef.current = true;

                                            try {
                                                 // ✅ VALIDACIÓN DE CLIENTE
                                                // if (!clienteSeleccionado) {
                                                //     Swal.fire({
                                                //         icon: 'warning',
                                                //         title: 'Cliente requerido',
                                                //         text: 'Debes seleccionar un cliente antes de agregar un análisis.',
                                                //         timer: 1800,
                                                //         showConfirmButton: false,
                                                        
                                                //     });
                                                //     return;
                                                // }

                                                const texto = e.target.value?.trim();
                                                if (!texto) return;

                                                // Buscar coincidencia exacta
                                                const encontrado = analisis.find(
                                                    a =>
                                                        a.descripcionTipoAnalisis
                                                            .toLowerCase()
                                                            .trim() === texto.toLowerCase()
                                                );

                                                if (!encontrado) return;

                                                // Evitar duplicados
                                                if (
                                                    analisisAgregados.some(
                                                        a => a.idTipoAnalisis === encontrado.idTipoAnalisis
                                                    )
                                                ) {
                                                    Swal.fire({
                                                        icon: 'warning',
                                                        title: 'Análisis ya agregado',
                                                        text: 'El análisis ya ha sido agregado.',
                                                        timer: 1200,
                                                        showConfirmButton: false,
                                                        toast: true,
                                                        position: 'top-end'
                                                    });
                                                    return;
                                                }

                                                // 🔹 Verificar si ya está seleccionado
                                                if (!analisisSeleccionado || analisisSeleccionado.idTipoAnalisis !== encontrado.idTipoAnalisis) {
                                                    // Primer Enter → solo seleccionar
                                                    await handleSeleccionAnalisis(encontrado);
                                                    setAnalisisSeleccionado(encontrado);
                                                    return; // no agregamos aún
                                                }

                                                // Agregar DIRECTAMENTE sin depender del state
                                                await handleAgregarAnalisis(encontrado);

                                                Swal.fire({
                                                    icon: 'success',
                                                    title: 'Análisis agregado',
                                                    text: 'Se agregó correctamente',
                                                    timer: 1200,
                                                    showConfirmButton: false,
                                                    toast: true,
                                                    position: 'top-end'
                                                });
                                            } finally {
                                                setAnalisisSeleccionado(null);
                                                setTimeout(() => {
                                                    enterLockRef.current = false;
                                                }, 150);
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid> */}
                        <Grid item xs={12} md={4}>
    <Autocomplete
        size="small"
        options={analisis}
        value={analisisSeleccionado}
        inputValue={inputAnalisisValue}
        onInputChange={(_, newInputValue) => {
            setInputAnalisisValue(newInputValue)
        }}
        getOptionLabel={(option) => option?.descripcionTipoAnalisis || ''}
        filterOptions={createRestrictiveFilter(
            ['descripcionTipoAnalisis'],
            20,
            analisisAgregados
        )}
        isOptionEqualToValue={(option, value) =>
            option?.idTipoAnalisis === value?.idTipoAnalisis
        }
        onChange={async (_, newValue, reason) => {
            // Solo reaccionar si se selecciona con click o Enter
            if (reason === 'selectOption') {
                // Evitar duplicados
                if (
                    analisisAgregados.some(
                        a => a.idTipoAnalisis === newValue.idTipoAnalisis
                    )
                ) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Análisis ya agregado',
                        text: 'El análisis ya ha sido agregado. No se pueden duplicar.',
                        timer: 1500,
                        showConfirmButton: false,
                        toast: true,
                        position: 'top-end'
                    });
                    setAnalisisSeleccionado(null);
                    setInputAnalisisValue('');
                    return;
                }

                setAnalisisListo(false);
                await handleSeleccionAnalisis(newValue);
                setAnalisisSeleccionado(newValue);
                setAnalisisListo(true);
            }

            // Si se limpia
            if (!newValue) {
                setAnalisisSeleccionado(null);
                setInputAnalisisValue('');
            }
        }}
        renderInput={(params) => (
            <TextField
                {...params}
                size="small"
                label="Análisis"
                variant="outlined"
                inputRef={refAutocompleteAnalisis}
                onKeyDown={async (e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();

                    // 🔹 Solo agregar si ya hay algo seleccionado
                    if (!analisisSeleccionado) return;

                    // Evitar duplicados
                    if (
                        analisisAgregados.some(
                            a => a.idTipoAnalisis === analisisSeleccionado.idTipoAnalisis
                        )
                    ) {
                        Swal.fire({
                            icon: 'warning',
                            title: 'Análisis ya agregado',
                            text: 'El análisis ya ha sido agregado.',
                            timer: 1200,
                            showConfirmButton: false,
                            toast: true,
                            position: 'top-end'
                        });
                        return;
                    }

                    await handleAgregarAnalisis(analisisSeleccionado);

                    Swal.fire({
                        icon: 'success',
                        title: 'Análisis agregado',
                        text: 'Se agregó correctamente',
                        timer: 1200,
                        showConfirmButton: false,
                        toast: true,
                        position: 'top-end'
                    });

                    setAnalisisSeleccionado(null);
                    setInputAnalisisValue('');
                    setAnalisisListo(false);

                    
                }}
            />
        )}
    />
</Grid>


                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth required size="small">
                                <InputLabel>Tipo Precio</InputLabel>
                                <Select
                                    size="small"
                                    value={tipoPrecio || ''} // Asegúrate de que 'sexo' tenga un valor válido
                                    onChange={handleTipoPrecioChange}
                                    required

                                >
                                    {tiposPrecio.map((tipoP) => (
                                        <MenuItem key={tipoP.idTipoPrecio} value={tipoP.idTipoPrecio}>
                                            {tipoP.descripcionTipoPrecio}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField size="small" label="Valor" type="number" value={valor} onChange={(e) => setValor(e.target.value)} fullWidth inputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                // size="small"
                                // label="Desc. (%)"
                                // type="number"
                                // value={descuento}
                                // onChange={(e) => setDescuento(e.target.value)}
                                // fullWidth
                                // helperText="Edición libre"
                                // inputProps={{ 
                                //     min: 0, 
                                //     max: 100,
                                //     step: 0.01
                                // }}

                                //  size="small"
                                // label="Desc. (%)"
                                // type="number"
                                // value={descuento}
                                // onChange={(e) => {
                                //     const nuevoValor = parseFloat(e.target.value) || 0;
                                //     setDescuento(nuevoValor);

                                //     // Recalcular total en vivo
                                //     const valorNumerico = parseFloat(valor) || 0;
                                //     const cantidadNumerica = parseInt(cantidad) || 1;
                                //     const descuentoMonto = (valorNumerico * nuevoValor) / 100;
                                //     const nuevoTotal = (valorNumerico - descuentoMonto) * cantidadNumerica;
                                //     // setTotal(nuevoTotal);  // total visible antes de agregar
                                // }}
                                // fullWidth
                                // helperText="Descuento editable"
                                // inputProps={{ min: 0, max: 100, step: 0.01 }}
                                //                             />

                                                                // size="small"
                                                                // label="Desc. (%)"
                                                                // type="number"
                                                                // value={descuento}
                                                                // fullWidth
                                                                // inputProps={{ readOnly: !puedeCambiarDescuento }}
                                                                // onChange={(e) => {
                                                                //     const nuevoValor = e.target.value;
                                                                //     setDescuento(nuevoValor);
                                
                                                                //     if (puedeCambiarDescuento && nuevoValor !== ultimoValorAutorizado) {
                                                                //         // Al cambiar el valor autorizado, se bloquea nuevamente
                                                                //         setPuedeCambiarDescuento(false);
                                                                //         setUltimoValorAutorizado(null); // Limpia la autorización anterior
                                                                //     }
                                                                // }}

                                                                // InputProps={{
                                                                //     endAdornment: !puedeCambiarDescuento && (
                                                                //         <IconButton onClick={() => setOpenModalDescuento(true)}>
                                                                //             {/* <LockIcon /> */}
                                                                //         </IconButton>
                                                                //     )
                                                                // }}


                                                               
  size="small"
  label="Desc. (%)"
  type="number"
  value={descuento}
  onChange={(e) => {
    const nuevoValor = parseFloat(e.target.value) || 0;
    if (isNaN(nuevoValor) || nuevoValor === 0) {
    // 🔄 volver a automático
    setDescuento(0);
    descuentoManualRef.current = false;
  } else {
    // ✍️ manual
    setDescuento(nuevoValor);
    descuentoManualRef.current = true;
  }
    
  }}
  fullWidth
  helperText="Descuento editable"
  inputProps={{
    min: 0,
    max: 100,
    step: 0.01,
    style: { textAlign: 'center' },
  }}

                                                            />
                        </Grid>
                        <Grid item xs={12} md={1}>
                            <TextField
                                size="small"
                                label="Cantidad"
                                type="number"
                                value={cantidad}
                                // onChange={(e) => {
                                //     const val = Number(e.target.value);
                                //     // si mete algo >1, forzamos a 1
                                //     if (val > 1) {
                                //         setCantidad(1);
                                //     } else if (val < 0) {
                                //         setCantidad(0);
                                //     } else {
                                //         setCantidad(val);
                                //     }
                                // }}
                                onChange={(e) => setCantidad(Math.max(1, Number(e.target.value)))}
                                fullWidth
                                inputProps={{ step: 1 }}
                            />

                        </Grid>
                        <Grid item xs={12} md={1}>
                            <FormControlLabel
                                size="small"
                                control={
                                    <Tooltip
                                        title={!poliza ? "El cliente no tiene una póliza válida para este origen" : ""}
                                    >
                                        <span>
                                            <Checkbox
                                                size="small"
                                                checked={elegible}
                                                onChange={(e) => setElegible(e.target.checked)}
                                                disabled={!poliza}
                                            />
                                        </span>
                                    </Tooltip>
                                }
                                label="Elegible"
                            />
                        </Grid>
                        <Grid item xs={12} style={{padding:'0px'}}>
                            <Button size="small" variant="contained" color="primary" onClick={handleAgregarAnalisis} disabled={!tienePermiso('Agregar Análisis a Cotización') || !analisisListo}>Agregar</Button>
                        </Grid>

                        {/* Tabla de análisis agregados */}
                        <Grid item xs={12}>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Acciones</TableCell>
                                            <TableCell>Modulo</TableCell>
                                            <TableCell>Análisis</TableCell>
                                            <TableCell>Cantidad</TableCell>
                                            <TableCell>Precio</TableCell>
                                            <TableCell>Descuento</TableCell>
                                            <TableCell>Total</TableCell>
                                            <TableCell>Elegible</TableCell>

                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                          {analisisFiltrados
                                            .slice(tablePage * rowsPerPage, tablePage * rowsPerPage + rowsPerPage)
                                            .map((analisis, index) => {
                                                const actualIndex = tablePage * rowsPerPage + index;
                                                return (
                                                    <TableRow key={actualIndex}>
                                                        <TableCell>
                                                            <IconButton onClick={() => handleEliminarAnalisis(actualIndex)} disabled={!tienePermiso('Eliminar Análisis de Cotización')}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                            <IconButton onClick={() => handleVerCondiciones(analisis)}>
                                                                <VisibilityIcon color="primary" />
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell>{analisis.idModulo}</TableCell>
                                                        <TableCell>{analisis.analisis}</TableCell>
                                                        <TableCell>{analisis.cantidad}</TableCell>
                                                        <TableCell>{analisis.precio}</TableCell>
                                                        {/* <TableCell>{parseFloat(analisis.descuento).toFixed(2)}</TableCell>
                                                        <TableCell>{parseFloat(analisis.total).toFixed(2)}</TableCell> */}
                                                        <TableCell>
                                                            {/* <Checkbox
                                                                checked={analisis.elegible}
                                                                onChange={() => handleElegibleChange(actualIndex)}
                                                            /> */}
                                                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                                                            <span>{parseFloat(analisis.descuento).toFixed(2)}</span>
                                                                                                                            {(() => {
                                                                                                                                const porcentaje = analisis.precio > 0 
                                                                                                                                    ? ((parseFloat(analisis.descuento) / parseFloat(analisis.precio)) * 100).toFixed(0) 
                                                                                                                                    : 0;
                                                                                                                                return porcentaje > 0 ? (
                                                                                                                                    <Box
                                                                                                                                        component="span"
                                                                                                                                        sx={{
                                                                                                                                            display: 'inline-flex',
                                                                                                                                            alignItems: 'center',
                                                                                                                                            justifyContent: 'center',
                                                                                                                                            minWidth: '32px',
                                                                                                                                            height: '32px',
                                                                                                                                            borderRadius: '50%',
                                                                                                                                            backgroundColor: '#4caf50',
                                                                                                                                            color: 'white',
                                                                                                                                            fontSize: '0.75rem',
                                                                                                                                            fontWeight: 'bold',
                                                                                                                                            padding: '4px'
                                                                                                                                        }}
                                                                                                                                    >
                                                                                                                                        {porcentaje}%
                                                                                                                                    </Box>
                                                                                                                                ) : null;
                                                                                                                            })()}
                                                                                                                        </Box>
                                                        </TableCell>
                                                        <TableCell>{parseFloat(analisis.total).toFixed(2)}</TableCell>
                                                         <TableCell>
                                                           <Checkbox
                                                            size="small"
                                                            checked={analisis.elegible}
                                                            disabled={!poliza}              // ← deshabilitado si no hay póliza
                                                            onChange={() => {
                                                            if (!poliza) return;           // ← protección adicional
                                                            handleElegibleChange(actualIndex);
                                                            }}
                                                                />
                                                         </TableCell>
                                                        <TableCell sx={{ maxWidth: 150, overflowX: 'auto', whiteSpace: 'nowrap' }}>
                                                            <Box component="div" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                                                                {analisis.comentarios}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow>
                                            <TableCell align="right">
                                                <strong>Cantidad Analisis:</strong>
                                            </TableCell>
                                            <TableCell><strong>{cantidadTotal}</strong></TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    component="div"
                                    count={analisisAgregados.length}
                                    rowsPerPage={rowsPerPage}
                                    page={tablePage}
                                    onPageChange={handleChangeTablePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    labelRowsPerPage="Filas por página:"
                                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                                />
                            </TableContainer>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextField size="small" label="Observaciones" value={observaciones || ''} fullWidth InputLabelProps={{ shrink: true }} onChange={(e) => setObservaciones(e.target.value)} />
                        </Grid>
                        <Grid container item xs={12} justifyContent="flex-end" spacing={2} sx={{ mt: 0.4 }}>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    size="small"
                                    label="Valor Venta"
                                    type="number"
                                    value={valorVenta}
                                    onChange={(e) => setValorVenta(e.target.value)} // Corregido
                                    fullWidth
                                    inputProps={{ readOnly: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    size="small"
                                    label="% Descuento"
                                    type="number"
                                    value={descuentoTotalP}
                                    onChange={(e) => setDescuentoTotalP(e.target.value)} // Corregido
                                    fullWidth
                                    inputProps={{ readOnly: true }} // Opcional: si deseas que sea solo lectura
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    size="small"
                                    label="Descuento"
                                    type="number"
                                    value={descuentoTotal}
                                    onChange={(e) => setDescuentoTotal(e.target.value)} // Corregido
                                    fullWidth
                                    inputProps={{ readOnly: true }} // Opcional: si deseas que sea solo lectura
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    size="small"
                                    label="Total a Pagar"
                                    type="number"
                                    value={totalPagar}
                                    onChange={(e) => setTotalPagar(e.target.value)} // Corregido
                                    fullWidth
                                    inputProps={{ readOnly: true }} // Opcional: si deseas que sea solo lectura
                                />
                            </Grid>
                        </Grid>
                        <Grid container item xs={12} justifyContent="flex-start" spacing={2} sx={{ mt: 0.4 }}>
                            <Grid item xs={12} md={3}>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        size="small"
                                        label="Poliza"
                                        value={poliza}
                                        onChange={(e) => setPoliza(e.target.value)}
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        size="small"
                                        label="Empresa"
                                        type="text"
                                        value={empresa}
                                        onChange={(e) => setEmpresa(e.target.value)}
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        size="small"
                                        label="N° transaccion"
                                        value={nTransaccion}
                                        onChange={(e) => setNTransaccion(e.target.value)}
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        size="small"
                                        label="Minimo"
                                        value={minimo}
                                        onChange={(e) => setMinimo(e.target.value)}
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        size="small"
                                        label="Maximo"
                                        type="text"
                                        value={maximo}
                                        onChange={(e) => setMaximo(e.target.value)}
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        size="small"
                                        label="N° verificacion"
                                        value={nVerificacion}
                                        onChange={(e) => setNVerificacion(e.target.value)}
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        size="small"
                                        label="% Copago"
                                        value={copago}
                                        onChange={(e) => setCopago(e.target.value)}
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <FormControlLabel
                                        size="small"
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={autorizado}
                                                onChange={(e) => setAutorizado(e.target.checked)}
                                            />
                                        }
                                        label="Autorizado"
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        size="small"
                                        label="Deducible"
                                        value={deducible}
                                        onChange={(e) => setDeducible(e.target.value)}
                                        fullWidth

                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        size="small"
                                        label="Elegible"
                                        value={valorElegible}
                                        onChange={(e) => setValorElegible(e.target.value)}
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        size="small"
                                        label="No Elegible"
                                        type="text"
                                        value={noElegibles}
                                        onChange={(e) => setNoElegibles(e.target.value)}
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        size="small"
                                        label="Aseguradora"
                                        value={aseguradora}
                                        onChange={(e) => setAseguradora(e.target.value)}
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        size="small"
                                        label="Copago"
                                        value={valorCopago}
                                        onChange={(e) => setValorCopago(e.target.value)}
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        size="small"
                                        label="Total Asegurado"
                                        value={totalAsegurado}
                                        onChange={(e) => setTotalAsegurado(e.target.value)}
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Botón para guardar cotización */}
                        <Grid item xs={12}>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={handleGuardarCotizacion}
                                disabled={isGuardando || !tienePermiso('Crear Cotización')}
                            >
                                {isGuardando ? 'Guardando...' : 'Guardar Cotización'}
                            </Button>
                        </Grid>

                    </Grid>
                    <Modal
                        open={openModalCliente}
                        onClose={handleCloseModalCliente}
                        aria-labelledby="modal-title"
                        aria-describedby="modal-description"
                    >
                        <div style={modalStyle}>
                            <GestionClientes
                                cliente={clienteSeleccionado}
                                onClienteGuardado={handleClienteActualizado}
                                onClose={handleCloseModalCliente}
                            />
                        </div>
                    </Modal>
                    {/* </div> */}
                    {/* </div> */}                    
                    <ResultadoModal
                        open={showModal}
                        onClose={() => setShowModal(false)}
                        resultado={apiResult}
                    />
                    {openModalCondiciones && selectedAnalisis && (
                        <CondicionesUtilidadesModal
                            open={openModalCondiciones}
                            onClose={() => setOpenModalCondiciones(false)}
                            // analysisId={selectedAnalisis.idTipoAnalisis}
                            analysisId={selectedAnalisis}
                        />
                    )}

                    {/* Modal de vista previa PDF para cotizaciones */}
                    <Modal
                        open={openReimprimirPreview}
                        onClose={handleCloseReimprimirPreview}
                        aria-labelledby="pdf-preview-modal-title"
                        aria-describedby="pdf-preview-modal-description"
                    >
                        <div style={modalStyle}>
                            <Typography id="pdf-preview-modal-title" variant="h6" component="h2" style={{ marginBottom: '16px' }}>
                                Vista Previa - Cotización
                            </Typography>
                            
                            {reimprimirLoading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                                    <CircularProgress />
                                    <Typography style={{ marginLeft: '16px' }}>Generando vista previa...</Typography>
                                </div>
                            ) : reimprimirError ? (
                                <div style={{ padding: '20px', textAlign: 'center' }}>
                                    <Typography color="error" variant="h6">Error al generar la vista previa</Typography>
                                    <Typography color="error">{reimprimirError}</Typography>
                                    <Button 
                                        variant="contained" 
                                        onClick={handleCloseReimprimirPreview}
                                        style={{ marginTop: '16px' }}
                                    >
                                        Cerrar
                                    </Button>
                                </div>                            ) : reimprimirPdfUrl ? (
                                <Box style={{ display: 'flex', flexDirection: 'column', height: 'calc(90vh - 100px)' }}>
                                    <Box style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <iframe
                                            src={reimprimirPdfUrl}
                                            style={{ width: '100%', height: '100%', border: 'none', flexGrow: 1 }}
                                            title="Vista previa de la cotización"
                                        />
                                    </Box>
                                    <Box mt={2} display="flex" justifyContent="center" gap={2}>
                                        <Button 
                                            variant="outlined" 
                                            color="secondary"
                                            onClick={() => {
                                                    handleCloseReimprimirPreview();
                                                    // Si estamos en modo modificar, cerrar el modal principal también
                                                    if (modo === 'modificar' && typeof onClose === 'function') {
                                                        onClose();
                                                    }
                                                }}
                                            size="large"
                                        >
                                            Cancelar
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            color="primary"
                                            onClick={handleEnviarWhatsapp}
                                            size="large"
                                        >
                                            WhatsApp
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            style={{ backgroundColor: 'green', color: 'white' }}
                                            onClick={handleCrearOrdenTrabajo}
                                            size="large"
                                        >
                                            Crear Orden de Trabajo
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            color="primary"
                                            onClick={() => {
                                                handleCloseReimprimirPreview();
                                                if (typeof onClose === 'function') {
                                                    onClose(); // Only call if it's actually a function
                                                }
                                                }}
                                            size="large"
                                        >
                                            Continuar
                                        </Button>
                                    </Box>
                                </Box>
                            ) : null}
                        </div>
                    </Modal>

                    {/* Modal para mostrar la orden de trabajo creada */}
                    <Modal
                        open={openOrdenTrabajoModal}
                        onClose={() => setOpenOrdenTrabajoModal(false)}
                        aria-labelledby="modal-title-orden-trabajo"
                        aria-describedby="modal-description-orden-trabajo"
                        disableEscapeKeyDown={false}
                        disableBackdropClick={false}
                    >
                        <div style={{
                            ...modalStyle,
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            display: 'block'
                        }}>
                             {/* {ordenTrabajoData && ( */}
                              {ordenTrabajoData && ordenTrabajoData.detalles?.length > 0 && (
                                <GestionOrdenTrabajo
                                    ordenTrabajo={ordenTrabajoData.ordenTrabajo}
                                    detalles={ordenTrabajoData.detalles}
                                    onClose={() => {
                                        setOpenOrdenTrabajoModal(false);
                                        // Cerrar el modal de cotización si sigue abierto
                                        if (typeof onClose === 'function') {
                                            onClose();
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </Modal> 
                  
                </div>
            </div>
        </>
    );
}

export default GestionCotizaciones;
